import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { API } from '@/utils/constants';
import { formatDate } from '@/utils/date';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { HelpTooltip } from '@/components/HelpTooltip';
import { printJobSticker } from '@/utils/sticker';
import { EmailStatusIcons } from '@/components/email-status/EmailStatusIcons';

// ── Payment / status chip definitions (module-scope) ───────────────────────
// Feb 2026 — hoisted from inside the Dashboard component so a load-time
// invariant (below) can assert every chip carries its own palette. This
// prevents the "missing colour" bug class that previously crashed the whole
// Dashboard when a new chip was added without a matching entry in a
// separate colour lookup map.
const PAYMENT_CHIP_DEFS = [
  { key: 'all',              label: 'All',                        test: () => true,
    activeBg: '#374151', activeColor: '#ffffff', activeBorder: '#374151' },
  { key: 'deposit',          label: '💵 Deposit paid',            test: (j) => j.deposit_received === true,
    activeBg: '#1e40af', activeColor: '#ffffff', activeBorder: '#1e40af' },
  { key: 'paid',             label: '💰 Paid',                    test: (j) => j.payment_received === true,
    activeBg: '#065f46', activeColor: '#ffffff', activeBorder: '#065f46' },
  { key: 'paid-uncollected', label: '💰 Paid – not collected',    test: (j) => j.payment_received === true && !j.closed,
    activeBg: '#0d9488', activeColor: '#ffffff', activeBorder: '#0d9488' },
  { key: 'unpaid-awaiting',  label: 'Un-paid awaiting',           test: (j) =>
      (j.payment_status || 'Unpaid') !== 'Paid'
      && !j.closed
      && (parseFloat(j.total_amount) || 0) > 0,
    activeBg: '#b45309', activeColor: '#ffffff', activeBorder: '#b45309' },
  { key: 'not-contacted',    label: '📞 Not yet contacted',        test: (j) =>
      !j.contacted
      && !j.closed
      && !['Finish', 'Not Recoverable', 'Not Proceeding'].includes(j.status || ''),
    activeBg: '#7c3aed', activeColor: '#ffffff', activeBorder: '#7c3aed' },
];

// Load-time invariant: every chip def MUST provide key, label, test, and a
// full active palette. Throws on module import if the shape is broken so a
// missing colour surfaces during the very first render (or CRA build), not
// as an "undefined.bg" crash mid-session. Cheap, single-pass, dev + prod.
PAYMENT_CHIP_DEFS.forEach((chip, i) => {
  const missing = ['key', 'label', 'test', 'activeBg', 'activeColor', 'activeBorder']
    .filter(k => chip[k] === undefined || chip[k] === null || chip[k] === '');
  if (missing.length) {
    throw new Error(`PAYMENT_CHIP_DEFS[${i}] (key=${chip.key || '?'}) missing required field(s): ${missing.join(', ')}. Every chip must declare its own colour palette so the render can't crash on undefined.`);
  }
});

// ── Days Owed column helper ────────────────────────────────────────────────
// Feb 2026 — replaces the old static "Paid / Unpaid" cell with an actionable
// urgency signal. For jobs that are workshop-done (Finish / Not Recoverable /
// Not Proceeding) AND still have a balance, show how many days have passed
// since the job was finished. Everything else returns show:false so the cell
// falls back to a dash. Colour bands per operator preference:
//   0–4  → neutral   (grey)  — collect soon
//   5–9  → warning   (amber) — nudge the customer
//   10+  → critical  (red)   — chase / send reminder
// Anchor date is `date_finished`, falling back to `date_out` → `date_in` so
// the cell degrades gracefully on legacy rows.
//
// CUTOFF (Feb 2026): only apply to jobs whose anchor date is on/after
// 2026-07-01. Older jobs predate the Brevo webhook tracking and legacy SMTP
// email logs, so we can't reliably tell whether an invoice was sent — the
// column stays a dash for those. Keeps the dashboard focused on the
// currently-actionable chase list rather than years-old data.
const QUALIFYING_OWED_STATUSES = ['Finish', 'Not Recoverable', 'Not Proceeding'];
const DAYS_OWED_CUTOFF_ISO = '2026-07-01';

const daysBetween = (isoDateStr) => {
  if (!isoDateStr) return null;
  try {
    // date_finished is a yyyy-MM-dd string; treat it as a local calendar day
    // to avoid the UTC skew that bit the "days_since_invoice" report earlier.
    const parts = String(isoDateStr).slice(0, 10).split('-');
    if (parts.length !== 3) return null;
    const anchor = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    if (Number.isNaN(anchor.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    anchor.setHours(0, 0, 0, 0);
    return Math.max(0, Math.floor((today - anchor) / (1000 * 60 * 60 * 24)));
  } catch {
    return null;
  }
};

export const computeDaysOwedInfo = (job, invoiceSentAt) => {
  const status = job?.status || '';
  const balance = Number(job?.balance_owing ?? (Number(job?.total_amount || 0) - Number(job?.amount_paid || 0)));
  if (!QUALIFYING_OWED_STATUSES.includes(status)) return { show: false };
  if (!(balance > 0)) return { show: false };
  const anchor = job.date_finished || job.date_out || job.date_in;
  // Feb 2026 cutoff: skip legacy jobs whose data predates Brevo webhook tracking.
  if (!anchor || String(anchor).slice(0, 10) < DAYS_OWED_CUTOFF_ISO) return { show: false };
  const days = daysBetween(anchor);
  if (days === null) return { show: false };
  const neverInvoiced = !invoiceSentAt;
  let band = 'neutral', bg = '#f1f5f9', fg = '#334155', border = '#e2e8f0';
  if (days >= 10)      { band = 'critical'; bg = '#fee2e2'; fg = '#991b1b'; border = '#fca5a5'; }
  else if (days >= 5)  { band = 'warning';  bg = '#fef3c7'; fg = '#92400e'; border = '#fcd34d'; }
  return { show: true, days, neverInvoiced, band, bg, fg, border, balance };
};

/**
 * Dashboard Component
 * Main dashboard showing job status overview and recent jobs
 */
const Dashboard = () => {
  // Jul 2026 — Filter state persisted to URL query params so that navigating
  // away (e.g. clicking a row to open a job) and coming back preserves the
  // active filters. Browser back/forward Just Works, and filter states are
  // now bookmarkable/shareable (e.g. /?status=Approved&chip=paid-uncollected).
  const [searchParams, setSearchParams] = useSearchParams();
  // Helpers to read initial values from the URL (called ONCE at mount via
  // the useState lazy initializer). After mount we treat local state as the
  // source of truth and write it back to the URL via a syncing effect below.
  const readUrl = (key, fallback = null) => searchParams.get(key) || fallback;

  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  // Feb 2026 — Brevo email status per job, keyed by job.id.
  //   { <jobId>: { quote?:{status,open_count,...}, invoice?:{...}, collection_notice?:{...} } }
  // Rendered as coloured icons after the Contact button.
  const [emailStatusByJob, setEmailStatusByJob] = useState({});
  const [searchTerm, setSearchTerm] = useState(() => readUrl('q', ''));
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(() => readUrl('status'));
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(() => readUrl('payment_status'));
  // Progress Status filter — operational status independent of the customer-facing `status`.
  // Values: null (all) | "Not Started" | "Imaging" | "WIP" | "Awaiting Parts"
  const [selectedProgressStatus, setSelectedProgressStatus] = useState(() => readUrl('progress'));

  // Feb 2026 — SMS modal state for the "📱 Payment Reminder" quick-action on
  // the Days Owed column. Reuses the /api/sms/send endpoint + sms_templates
  // stored in settings (see AllJobsPage for the equivalent flow).
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [smsJob, setSmsJob] = useState(null);
  const [smsMessage, setSmsMessage] = useState('');

  // ── SMS Inbox (Feb 2026) ────────────────────────────────────────────
  // Dashboard panel showing inbound SMS replies from customers. Polls
  // every 15s; triggers a chime + Web Notification on new unread arrivals.
  const [smsInboxThreads, setSmsInboxThreads] = useState([]);
  const [smsInboxTotalUnread, setSmsInboxTotalUnread] = useState(0);
  const [smsThreadOpen, setSmsThreadOpen] = useState(false);
  const [smsThreadData, setSmsThreadData] = useState(null);
  const [smsReplyText, setSmsReplyText] = useState('');
  const [smsReplySending, setSmsReplySending] = useState(false);
  const [smsNotifPermission, setSmsNotifPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const prevUnreadRef = React.useRef(0);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [sendingSms, setSendingSms] = useState(false);

  // Jul 2026 — payment-flag chip filter (deposit / paid pills on the table).
  // Values: 'all' | 'deposit' | 'paid' | 'paid-uncollected' | 'unpaid-awaiting'
  // Composes on top of everything else (status card, progress status, search).
  // Rendered as chip row directly above the jobs table.
  const [paymentFlagFilter, setPaymentFlagFilter] = useState(() => readUrl('chip', 'all'));

  // Jul 2026 — Filter presets. Server-synced per user so they follow the
  // operator across devices (phone / laptop / workshop PC). localStorage is
  // still used as an offline cache + fallback while the fetch is in flight.
  // Each preset is `{ id, name, params: {status, payment_status, progress, chip, q} }`.
  const PRESET_KEY = 'dashboard-filter-presets-v1';
  const [filterPresets, setFilterPresets] = useState(() => {
    try {
      const raw = localStorage.getItem(PRESET_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [presetPopoverOpen, setPresetPopoverOpen] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  // Note: Job deletion has been removed to protect record integrity.
  // Use job statuses ("Not Proceeding", "Not Recoverable") instead — every job stays in the system permanently.
  const [recentJobs, setRecentJobs] = useState([]);
  const [revenueData, setRevenueData] = useState({ data: [], total_revenue: 0, total_jobs: 0 });
  const [revenuePeriod, setRevenuePeriod] = useState('weekly');
  const [revenueStartDate, setRevenueStartDate] = useState('');
  const [revenueEndDate, setRevenueEndDate] = useState('');
  // Revenue drill-down modal
  const [revenueJobsModal, setRevenueJobsModal] = useState({ open: false, jobs: [], total_revenue: 0, total_jobs: 0, title: '', day: null });
  const [revenueJobsLoading, setRevenueJobsLoading] = useState(false);
  // General Reminders state
  const [generalReminders, setGeneralReminders] = useState([]);
  const [showReminderModal, setShowReminderModal] = useState(false);
  // Stale Booked purchases/expenses (needs follow-up)
  const [bookedStale, setBookedStale] = useState({ threshold_days: 30, stale_purchases: [], stale_expenses: [], count: 0 });
  // Jobs Due Soon — full-list dialog (opens when user clicks a bucket header or "+N more")
  const [dueBucketDialog, setDueBucketDialog] = useState({ open: false, title: '', icon: '', jobs: [] });
  const openDueBucket = (title, icon, jobs) => setDueBucketDialog({ open: true, title, icon, jobs: jobs || [] });
  const [newReminder, setNewReminder] = useState({
    title: '',
    description: '',
    due_date: '',
    due_time: '',
    priority: 'Normal'
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Refresh data when navigating to dashboard (e.g., after completing a reminder)
  useEffect(() => {
    fetchJobs();
    fetchSettings();
    fetchRecentJobs();
    fetchRevenueData();
    fetchGeneralReminders();
    fetchBookedStale();
    checkBrowserNotifications();
    // Feb 2026 — SMS inbox initial load + 15s polling for new inbound SMS.
    // `silent: true` on the first load avoids chiming on dashboard entry.
    fetchSmsInbox({ silent: true });
    const smsPoll = setInterval(() => fetchSmsInbox(), 15000);
    return () => clearInterval(smsPoll);
  }, [location.key]);

  const fetchBookedStale = async () => {
    try {
      const r = await axios.get(`${API}/dashboard/booked-transactions`);
      setBookedStale(r.data || { threshold_days: 30, stale_purchases: [], stale_expenses: [], count: 0 });
    } catch (_) {
      // Non-fatal — the reminder card is optional info.
    }
  };

  // Fetch revenue data when period changes (only for non-custom periods)
  useEffect(() => {
    if (revenuePeriod !== 'custom') {
      fetchRevenueData();
    }
  }, [revenuePeriod]);

  // Jul 2026 — Sync active filters → URL query string. Uses `replace: true`
  // so it doesn't pollute browser history with a new entry every keystroke,
  // yet still makes the URL reflect the current filters so navigating away
  // and coming back restores the same view.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const setOrDelete = (key, value, isDefault) => {
      if (value && !isDefault) next.set(key, value);
      else next.delete(key);
    };
    setOrDelete('status', selectedStatus);
    setOrDelete('payment_status', selectedPaymentStatus);
    setOrDelete('progress', selectedProgressStatus);
    setOrDelete('chip', paymentFlagFilter, paymentFlagFilter === 'all');
    setOrDelete('q', searchTerm && searchTerm.trim());
    // Only write when the URL actually changed to avoid a re-render loop.
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [selectedStatus, selectedPaymentStatus, selectedProgressStatus, paymentFlagFilter, searchTerm]);

  // Jul 2026 — Persist filterPresets:
  //  1) localStorage cache (instant, offline)
  //  2) server (via PUT) so the presets follow the user across devices.
  // The PUT is fire-and-forget with a silent error toast — if the API is
  // unreachable, the localStorage cache still keeps things usable this session.
  useEffect(() => {
    try { localStorage.setItem(PRESET_KEY, JSON.stringify(filterPresets)); } catch (_) { /* quota / private-mode — silent */ }
  }, [filterPresets]);

  // Feb 2026 — Auto-poll Brevo email status every 30s so opened/delivered
  // events flip the icons without the user having to refresh. The endpoint
  // is tiny (~200 bytes per job with tracked emails, jobs without emails
  // aren't returned) so this is very cheap. Pauses when the tab is hidden.
  useEffect(() => {
    let cancelled = false;
    const refreshStatuses = async () => {
      if (document.hidden) return;
      try {
        const r = await axios.get(`${API}/jobs/email-status-batch?days=180`);
        if (!cancelled) setEmailStatusByJob(r.data.jobs || {});
      } catch (_) { /* non-critical */ }
    };
    const id = setInterval(refreshStatuses, 30000);
    // Also refresh immediately when the tab becomes visible again
    const onVis = () => { if (!document.hidden) refreshStatuses(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);



  // Fetch server-synced presets on mount. If there are none server-side but the
  // browser has local ones (first login after this feature rolls out), push
  // them up so nothing gets lost during the migration.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/user/filter-presets`);
        if (cancelled) return;
        const serverPresets = Array.isArray(r.data?.presets) ? r.data.presets : [];
        if (serverPresets.length === 0 && filterPresets.length > 0) {
          // Migrate local → server (one-time on first fetch).
          try {
            await axios.put(`${API}/user/filter-presets`, { presets: filterPresets });
          } catch (_) { /* silent */ }
        } else {
          // Server is source of truth once it has data. Overwrite local cache.
          setFilterPresets(serverPresets);
        }
      } catch (_) { /* offline or 401 — keep localStorage cache */ }
    })();
    return () => { cancelled = true; };
    // Intentionally empty deps — runs once on mount.
  }, []);

  // Push presets to server whenever they change (debounced by React batching).
  // Fires-and-forgets; failures fall back to localStorage cache silently.
  const syncPresetsToServer = async (next) => {
    try { await axios.put(`${API}/user/filter-presets`, { presets: next }); }
    catch (_) { /* silent — cache still holds the change */ }
  };

  // Apply a preset — writes its params to the URL. The state-init effects
  // above already pull from URL, and the state→URL sync effect will then
  // put things back if they've drifted. To make apply immediate without
  // waiting for a re-mount, we also set state directly.
  const applyPreset = (preset) => {
    const p = preset.params || {};
    setSelectedStatus(p.status || null);
    setSelectedPaymentStatus(p.payment_status || null);
    setSelectedProgressStatus(p.progress || null);
    setPaymentFlagFilter(p.chip || 'all');
    setSearchTerm(p.q || '');
    setPresetPopoverOpen(false);
    toast.success(`Applied preset "${preset.name}"`);
  };

  const saveCurrentAsPreset = () => {
    const name = newPresetName.trim();
    if (!name) return;
    // Overwrite an existing preset with the same name (case-insensitive) so
    // "save" doubles as "update" — matches how most preset UIs behave.
    const existing = filterPresets.filter(p => p.name.toLowerCase() !== name.toLowerCase());
    const preset = {
      id: `preset-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name,
      params: {
        status: selectedStatus || null,
        payment_status: selectedPaymentStatus || null,
        progress: selectedProgressStatus || null,
        chip: paymentFlagFilter !== 'all' ? paymentFlagFilter : null,
        q: (searchTerm && searchTerm.trim()) || null,
      },
    };
    const next = [preset, ...existing];
    setFilterPresets(next);
    syncPresetsToServer(next);
    setNewPresetName('');
    toast.success(`Saved preset "${name}"`);
  };

  const deletePreset = (id) => {
    setFilterPresets(prev => {
      const next = prev.filter(p => p.id !== id);
      syncPresetsToServer(next);
      return next;
    });
  };

  // Small human-readable summary of a preset for the row subtitle.
  const summarizePreset = (params = {}) => {
    const bits = [];
    if (params.status) bits.push(params.status);
    if (params.payment_status) bits.push(`${params.payment_status} payment`);
    if (params.progress) bits.push(params.progress);
    if (params.chip) {
      const nice = { deposit: '💵 Deposit', paid: '💰 Paid', 'paid-uncollected': '💰 Paid – not collected', 'unpaid-awaiting': 'Un-paid awaiting' };
      bits.push(nice[params.chip] || params.chip);
    }
    if (params.q) bits.push(`"${params.q}"`);
    return bits.length === 0 ? 'All jobs (no filters)' : bits.join(' · ');
  };

  // True when the current filter state is anything but the "no filter" default.
  const hasAnyActiveFilter = !!(selectedStatus || selectedPaymentStatus || selectedProgressStatus || (paymentFlagFilter && paymentFlagFilter !== 'all') || (searchTerm && searchTerm.trim()));

  // Jul 2026 — Derive the visible `jobs` list from `allJobs` + active filters
  // whenever ANY filter changes. This is the single source of truth for what
  // rows appear in the table. Without this, on initial mount with URL-restored
  // filters (e.g. `/?status=Approved`), the status card would show as "selected"
  // but the table would still list every job — because the initial fetchJobs
  // doesn't know about the URL filters. Runs after every allJobs refresh too,
  // so filters survive background refetches.
  //
  // NOTE: The existing click handlers still do their own `setJobs(...)` calls
  // for snappier UX — those get harmlessly overwritten by this effect on the
  // next tick, but the state stays consistent.
  useEffect(() => {
    let out = allJobs;
    if (selectedStatus) out = out.filter(j => j.status === selectedStatus);
    if (selectedPaymentStatus === 'Awaiting') {
      // "Awaiting Payment" bucket = job.status = Finish + not paid, per existing UX.
      out = out.filter(j => j.status === 'Finish' && (j.payment_status || 'Unpaid') !== 'Paid');
    } else if (selectedPaymentStatus) {
      out = out.filter(j => (j.payment_status || 'Unpaid') === selectedPaymentStatus);
    }
    if (selectedProgressStatus) {
      out = out.filter(j => (j.progress_status || 'Not Started') === selectedProgressStatus && !j.closed);
    }
    setJobs(out);
  }, [allJobs, selectedStatus, selectedPaymentStatus, selectedProgressStatus]);

  // Check and show browser notifications
  const checkBrowserNotifications = async () => {
    try {
      // First check if notifications are enabled in settings
      const settingsRes = await axios.get(`${API}/settings`);
      if (!settingsRes.data?.browser_notifications_enabled) return;
      
      // Check browser permission
      if (!('Notification' in window) || Notification.permission !== 'granted') return;
      
      // Get notification data
      const response = await axios.get(`${API}/notifications/due-today`);
      const { overdue_jobs, jobs_due_today, reminders_due_today } = response.data;
      
      // Show notifications
      if (overdue_jobs?.length > 0) {
        new Notification('⚠️ Overdue Jobs', {
          body: `You have ${overdue_jobs.length} overdue job(s) that need attention.`,
          icon: '/favicon.ico',
          tag: 'overdue-jobs'
        });
      }
      
      if (jobs_due_today?.length > 0) {
        new Notification('📅 Jobs Due Today', {
          body: `${jobs_due_today.length} job(s) are due today.`,
          icon: '/favicon.ico',
          tag: 'jobs-due'
        });
      }
      
      if (reminders_due_today?.length > 0) {
        new Notification('🔔 Reminders', {
          body: `You have ${reminders_due_today.length} reminder(s) for today.`,
          icon: '/favicon.ico',
          tag: 'reminders'
        });
      }
    } catch (error) {
      console.error('Error checking notifications:', error);
    }
  };

  const fetchRecentJobs = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/recent-jobs?limit=10`);
      setRecentJobs(response.data);
    } catch (error) {
      console.error('Error fetching recent jobs:', error);
    }
  };

  const fetchGeneralReminders = async () => {
    try {
      const response = await axios.get(`${API}/reminders`);
      setGeneralReminders(response.data);
    } catch (error) {
      console.error('Error fetching general reminders:', error);
    }
  };

  const handleCreateReminder = async () => {
    if (!newReminder.title.trim()) {
      toast.error('Please enter a task name');
      return;
    }
    if (!newReminder.due_date) {
      toast.error('Please select a due date');
      return;
    }

    try {
      await axios.post(`${API}/reminders`, newReminder);
      toast.success('Reminder created!');
      setShowReminderModal(false);
      setNewReminder({
        title: '',
        description: '',
        due_date: '',
        due_time: '',
        priority: 'Normal'
      });
      fetchGeneralReminders();
    } catch (error) {
      console.error('Error creating reminder:', error);
      toast.error('Failed to create reminder');
    }
  };

  const handleCompleteGeneralReminder = async (reminderId) => {
    try {
      await axios.put(`${API}/reminders/${reminderId}/complete`);
      toast.success('Reminder marked as complete!');
      fetchGeneralReminders();
    } catch (error) {
      console.error('Error completing reminder:', error);
      toast.error('Failed to complete reminder');
    }
  };

  const fetchRevenueData = async (customStart = null, customEnd = null) => {
    try {
      let url = `${API}/dashboard/revenue-chart?period=${revenuePeriod}`;
      if (customStart && customEnd) {
        url = `${API}/dashboard/revenue-chart?period=custom&start_date=${customStart}&end_date=${customEnd}`;
      }
      const response = await axios.get(url);
      setRevenueData(response.data);
    } catch (error) {
      console.error('Error fetching revenue data:', error);
    }
  };

  const handleRevenueCustomDates = () => {
    if (revenueStartDate && revenueEndDate) {
      setRevenuePeriod('custom');
      fetchRevenueData(revenueStartDate, revenueEndDate);
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  const handleRevenuePeriodChange = (period) => {
    setRevenuePeriod(period);
    if (period !== 'custom') {
      setRevenueStartDate('');
      setRevenueEndDate('');
    }
  };

  const openRevenueJobsModal = async (day = null) => {
    setRevenueJobsLoading(true);
    setRevenueJobsModal({ open: true, jobs: [], total_revenue: 0, total_jobs: 0, title: 'Loading...', day });
    try {
      const params = new URLSearchParams({ period: revenuePeriod });
      if (revenuePeriod === 'custom' && revenueStartDate && revenueEndDate) {
        params.append('start_date', revenueStartDate);
        params.append('end_date', revenueEndDate);
      }
      if (day) params.append('day', day);
      const response = await axios.get(`${API}/dashboard/revenue-jobs?${params.toString()}`);
      const periodLabel = day
        ? `for ${day}`
        : revenuePeriod === 'weekly'
          ? '(Last 7 Days)'
          : revenuePeriod === 'monthly'
            ? '(Last 30 Days)'
            : revenueStartDate && revenueEndDate
              ? `(${revenueStartDate} → ${revenueEndDate})`
              : '';
      setRevenueJobsModal({
        open: true,
        jobs: response.data.jobs || [],
        total_revenue: response.data.total_revenue || 0,
        total_jobs: response.data.total_jobs || 0,
        title: `Completed Jobs ${periodLabel}`,
        day,
      });
    } catch (error) {
      console.error('Error loading revenue jobs:', error);
      setRevenueJobsModal({ open: false, jobs: [], total_revenue: 0, total_jobs: 0, title: '', day: null });
    } finally {
      setRevenueJobsLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      setSmsTemplates(response.data.sms_templates || []);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  // ── SMS Payment Reminder — pre-fills the Payment Reminder template so the
  // operator can review before hitting Send. Falls back to a generic message
  // if the template has been renamed/removed from settings.
  const openPaymentReminderSms = (job) => {
    setSmsJob(job);
    const businessName = settings?.business_info?.business_name || settings?.business_name || '';
    const businessPhone = settings?.business_info?.phone || settings?.business_phone || '';
    const balance = parseFloat(job.balance_owing ?? ((job.total_amount || 0) - (job.amount_paid || 0))).toFixed(2);
    const tpl = (smsTemplates || []).find(t => (t.name || '').toLowerCase().includes('payment reminder'));
    let msg = tpl?.message || 'Hi {customer_name}, a friendly reminder that ${balance_owing} is owing for job #{job_number}. Thanks, {business_name}';
    msg = msg
      .replace('{customer_name}', job.customer_name || '')
      .replace('{job_number}', job.job_number || '')
      .replace('{status}', job.status || '')
      .replace('{balance_owing}', balance)
      .replace('{business_name}', businessName)
      .replace('{business_phone}', businessPhone);
    setSmsMessage(msg);
    setSmsModalOpen(true);
  };

  const applySmsTemplate = (template) => {
    if (!smsJob) return;
    const businessName = settings?.business_info?.business_name || settings?.business_name || '';
    const businessPhone = settings?.business_info?.phone || settings?.business_phone || '';
    const balance = parseFloat(smsJob.balance_owing ?? ((smsJob.total_amount || 0) - (smsJob.amount_paid || 0))).toFixed(2);
    let msg = template.message || '';
    msg = msg
      .replace('{customer_name}', smsJob.customer_name || '')
      .replace('{job_number}', smsJob.job_number || '')
      .replace('{status}', smsJob.status || '')
      .replace('{balance_owing}', balance)
      .replace('{business_name}', businessName)
      .replace('{business_phone}', businessPhone);
    setSmsMessage(msg);
  };

  const sendPaymentReminderSms = async () => {
    if (!smsJob || !smsMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    setSendingSms(true);
    try {
      await axios.post(`${API}/sms/send`, {
        job_id: smsJob.id,
        message: smsMessage,
      });
      toast.success(`SMS sent to ${smsJob.customer_name || 'customer'}`);
      setSmsModalOpen(false);
      setSmsJob(null);
      setSmsMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSendingSms(false);
    }
  };

  // ── SMS Inbox: fetch + chime + notifications ─────────────────────────
  const playSmsChime = () => {
    try {
      // Short double-tone chime using WebAudio so we don't bundle an mp3.
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      [
        { freq: 880,  start: 0,    dur: 0.13 },
        { freq: 1320, start: 0.13, dur: 0.18 },
      ].forEach(({ freq, start, dur }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + start);
        gain.gain.setValueAtTime(0, now + start);
        gain.gain.linearRampToValueAtTime(0.15, now + start + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + start);
        osc.stop(now + start + dur);
      });
      setTimeout(() => { try { ctx.close(); } catch {} }, 1000);
    } catch { /* audio failed — not critical */ }
  };

  const fetchSmsInbox = async ({ silent = false } = {}) => {
    try {
      const resp = await axios.get(`${API}/sms/inbox`);
      const threads = resp.data?.threads || [];
      const total = resp.data?.total_unread || 0;
      // Chime + browser notification when unread count INCREASES during polling.
      if (!silent && total > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        const newCount = total - prevUnreadRef.current;
        playSmsChime();
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          try {
            // Use the top thread as the notification source so it's informative.
            const top = threads[0];
            const title = newCount === 1
              ? `SMS from ${top?.customer_name || 'customer'}`
              : `${newCount} new SMS replies`;
            const body = top?.latest_message?.slice(0, 140) || 'Open the dashboard to view';
            // eslint-disable-next-line no-new
            new Notification(title, { body, tag: 'sms-inbox' });
          } catch { /* notification unavailable */ }
        }
      }
      prevUnreadRef.current = total;
      setSmsInboxThreads(threads);
      setSmsInboxTotalUnread(total);
    } catch (error) {
      // Silent fail on polling; show toast only on manual load.
      if (!silent) console.error('Failed to load SMS inbox:', error);
    }
  };

  const requestSmsNotifPermission = async () => {
    if (typeof Notification === 'undefined') return;
    try {
      const perm = await Notification.requestPermission();
      setSmsNotifPermission(perm);
      if (perm === 'granted') toast.success('Notifications enabled for new SMS replies');
    } catch { /* denied or unavailable */ }
  };

  const openSmsThread = async (jobId) => {
    try {
      const resp = await axios.get(`${API}/sms/inbox/${jobId}/thread`);
      setSmsThreadData(resp.data);
      setSmsReplyText('');
      setSmsThreadOpen(true);
      // Mark as read in the background.
      await axios.post(`${API}/sms/inbox/${jobId}/mark-read`);
      // Refresh inbox so the badge updates — silent to avoid chime loop.
      fetchSmsInbox({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open SMS thread');
    }
  };

  const sendSmsReplyFromInbox = async () => {
    if (!smsThreadData || !smsReplyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }
    setSmsReplySending(true);
    try {
      await axios.post(`${API}/sms/send`, {
        job_id: smsThreadData.job_id,
        message: smsReplyText,
      });
      toast.success(`Reply sent to ${smsThreadData.customer_name || 'customer'}`);
      setSmsReplyText('');
      // Refresh the thread to show the sent message.
      const refreshed = await axios.get(`${API}/sms/inbox/${smsThreadData.job_id}/thread`);
      setSmsThreadData(refreshed.data);
      fetchSmsInbox({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSmsReplySending(false);
    }
  };

  // ── Inline status edit (Feb 2026) ──────────────────────────────────────
  // Click the Status pill → popover with the status list → pick one → PUT.
  // Keeps the backend audit log + Not-Proceeding tracking in the existing
  // /jobs/{id} endpoint; no side-effect handling here (operator opens the
  // job card afterward if they need to set date_finished, raise invoice,
  // etc.). Only "soft" status changes are the common use case per operator.
  const [statusSavingJobId, setStatusSavingJobId] = useState(null);
  const [statusPopoverJobId, setStatusPopoverJobId] = useState(null);

  const inlineUpdateStatus = async (job, newStatus) => {
    if (!job || !newStatus || newStatus === job.status) {
      setStatusPopoverJobId(null);
      return;
    }
    setStatusSavingJobId(job.id);
    const prevStatus = job.status;
    // Optimistic update so the pill colour swaps immediately.
    setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, status: newStatus } : j)));
    try {
      await axios.put(`${API}/jobs/${job.id}`, { status: newStatus });
      toast.success(`Job #${job.job_number} → ${newStatus}`);
      setStatusPopoverJobId(null);
    } catch (error) {
      // Roll back the optimistic update on failure.
      setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, status: prevStatus } : j)));
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setStatusSavingJobId(null);
    }
  };
  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchStockItems = async () => {
    try {
      const response = await axios.get(`${API}/stock-items`);
      setStockItems(response.data);
    } catch (error) {
      console.error('Error fetching stock items:', error);
    }
  };

  const fetchPurchases = async () => {
    try {
      const response = await axios.get(`${API}/purchases`);
      setPurchases(response.data);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    }
  };


  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  // Feb 2026 — Dashboard-level "Contacted" toggle. Optimistic UI update
  // (flip local state first, then confirm with the server) so the row
  // reacts instantly when the operator is smashing through a call-list.
  // On failure, revert. Response returns the full updated job so we sync
  // contacted_at / contacted_by / contacted_at_status back into state.
  const handleToggleContacted = async (jobId) => {
    const applyPatch = (patch) => {
      setAllJobs(prev => prev.map(j => j.id === jobId ? { ...j, ...patch } : j));
      setJobs(prev    => prev.map(j => j.id === jobId ? { ...j, ...patch } : j));
    };
    const prevJob = allJobs.find(j => j.id === jobId);
    if (!prevJob) return;
    const nextState = !prevJob.contacted;
    // Optimistic
    applyPatch({ contacted: nextState });
    try {
      const res = await axios.post(`${API}/jobs/${jobId}/contacted/toggle`);
      const j = res.data || {};
      applyPatch({
        contacted:            !!j.contacted,
        contacted_at:         j.contacted_at || null,
        contacted_by:         j.contacted_by || "",
        contacted_at_status:  j.contacted_at_status || "",
      });
      bumpJobsCache();
    } catch (error) {
      console.error('Error toggling contacted:', error);
      toast.error('Failed to update contacted flag');
      applyPatch({
        contacted:            prevJob.contacted,
        contacted_at:         prevJob.contacted_at,
        contacted_by:         prevJob.contacted_by,
        contacted_at_status:  prevJob.contacted_at_status,
      });
    }
  };
  // Feb 2026 — sessionStorage-backed cache for the /api/jobs list. Rationale:
  // the dashboard is the highest-traffic screen — opening any job and coming
  // back was refetching all ~1000 jobs (~2 MB) each time. With this cache
  // the second-onwards visit renders immediately from the last snapshot
  // (typically < 30s old) while a fresh copy loads silently in the
  // background. `bumpJobsCache()` is exposed so mutation handlers (deposit,
  // payment, contacted, etc.) can invalidate on demand.
  const JOBS_CACHE_KEY = 'dashboard.jobs.v1';
  const JOBS_CACHE_TTL_MS = 30_000;   // 30 s soft-TTL; older snapshots still render, then background refresh replaces.
  const readJobsCache = () => {
    try {
      const raw = sessionStorage.getItem(JOBS_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.jobs)) return null;
      return parsed;   // { jobs, ts }
    } catch { /* ignore malformed cache */ return null; }
  };
  const writeJobsCache = (jobs) => {
    try { sessionStorage.setItem(JOBS_CACHE_KEY, JSON.stringify({ jobs, ts: Date.now() })); } catch { /* quota / private-mode — ignore */ }
  };
  const bumpJobsCache = () => { try { sessionStorage.removeItem(JOBS_CACHE_KEY); } catch { /* ignore */ } };

  const fetchJobs = async ({ silent = false, forceNetwork = false } = {}) => {
    // Serve from cache first (unless the caller explicitly wants network).
    if (!forceNetwork) {
      const cached = readJobsCache();
      if (cached && Array.isArray(cached.jobs)) {
        setAllJobs(cached.jobs);
        setJobs(cached.jobs);
        setLoading(false);
        // Fresh enough — skip the network round-trip entirely.
        if (Date.now() - (cached.ts || 0) < JOBS_CACHE_TTL_MS) return;
        // Stale but still useful — fall through to a background refresh
        // while the UI already shows the cached rows.
        silent = true;
      }
    }
    try {
      if (!silent) setLoading(true);
      const response = await axios.get(`${API}/jobs`);
      const activeJobs = response.data.filter(job => !job.closed);
      writeJobsCache(activeJobs);
      setAllJobs(activeJobs);
      setJobs(activeJobs);
      // Feb 2026 — fetch Brevo email status batch in parallel (not awaited
      // above so the job rows render immediately; icons pop in a beat later)
      axios.get(`${API}/jobs/email-status-batch?days=180`)
        .then(r => setEmailStatusByJob(r.data.jobs || {}))
        .catch(() => {/* non-critical — icons just don't render */});
    } catch (error) {
      console.error('Error fetching jobs:', error);
      if (!silent) toast.error('Failed to load jobs');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/jobs?search=${searchTerm}`);
      // When searching, show ALL jobs (including closed ones) that match the search
      const searchResults = response.data;
      setAllJobs(searchResults);
      // Apply filters if selected
      let filtered = searchResults;
      if (selectedStatus) {
        filtered = filtered.filter(job => job.status === selectedStatus);
      }
      if (selectedPaymentStatus) {
        filtered = filtered.filter(job => (job.payment_status || 'Unpaid') === selectedPaymentStatus);
      }
      setJobs(filtered);
    } catch (error) {
      console.error('Error searching jobs:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusClick = (status) => {
    if (selectedStatus === status) {
      // If clicking the same status, clear filter
      setSelectedStatus(null);
      setJobs(allJobs);
    } else {
      // Filter jobs by selected status
      setSelectedStatus(status);
      setJobs(allJobs.filter(job => job.status === status));
    }
  };

  // Payment-flag chip filter — layered on top of everything else via useMemo.
  // Applied to the already-status-filtered `jobs` array so search + status
  // card + chip all compose naturally. Statuses used in "still on the shelf"
  // buckets are inferred from `job.closed` (any job you haven't closed is
  // still with you), matching how the rest of the app defines "active".
  const visibleJobs = useMemo(() => {
    switch (paymentFlagFilter) {
      case 'deposit':
        return jobs.filter(j => j.deposit_received === true);
      case 'paid':
        return jobs.filter(j => j.payment_received === true);
      case 'paid-uncollected':
        return jobs.filter(j => j.payment_received === true && !j.closed);
      case 'unpaid-awaiting':
        return jobs.filter(j =>
          (j.payment_status || 'Unpaid') !== 'Paid'
          && !j.closed
          && (parseFloat(j.total_amount) || 0) > 0
        );
      case 'not-contacted':
        // Feb 2026 — customer hasn't been rung for the current status yet.
        // Excludes closed jobs (no point contacting a closed job) and
        // terminal statuses where contact is meaningless.
        return jobs.filter(j =>
          !j.contacted
          && !j.closed
          && !['Finish', 'Not Recoverable', 'Not Proceeding'].includes(j.status || '')
        );
      case 'all':
      default:
        return jobs;
    }
  }, [jobs, paymentFlagFilter]);

  // Payment/status chip definitions live at module scope (PAYMENT_CHIP_DEFS
  // near the top of the file) with a load-time invariant that guarantees
  // every chip carries its own palette.
  const paymentChipDefs = PAYMENT_CHIP_DEFS;

  // Filter by progress status (operational). Toggles when clicking the same card again.
  const handleProgressStatusClick = (progressStatus) => {
    if (selectedProgressStatus === progressStatus) {
      setSelectedProgressStatus(null);
      setJobs(allJobs);
    } else {
      setSelectedProgressStatus(progressStatus);
      setSelectedStatus(null);
      setSelectedPaymentStatus(null);
      setJobs(allJobs.filter(job => (job.progress_status || 'Not Started') === progressStatus && !job.closed));
    }
  };

  const clearFilters = () => {
    setSelectedStatus(null);
    setSelectedPaymentStatus(null);
    setSelectedProgressStatus(null);
    setSearchTerm('');
    setJobs(allJobs);
  };

  const handleCloseJob = async (job) => {
    try {
      await axios.put(`${API}/jobs/${job.id}`, { closed: true });
      toast.success(`Job #${job.job_number} closed! You can reopen it from "All Jobs" page if needed.`);
      bumpJobsCache();
      fetchJobs({ forceNetwork: true });
    } catch (error) {
      console.error('Error closing job:', error);
      toast.error('Failed to close job');
    }
  };

  // Calculate status counts from all jobs
  const getStatusCounts = () => {
    const counts = {};
    if (settings && settings.statuses) {
      settings.statuses.forEach(status => {
        counts[status] = allJobs.filter(job => job.status === status).length;
      });
    }
    return counts;
  };

  const statusCounts = getStatusCounts();

  // Calculate jobs due in next 1-5 days
  const getJobsDueSoon = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dueSoon = {
      overdue: [],
      today: [],
      day1: [],
      day2: [],
      day3: [],
      day4: [],
      day5: []
    };
    
    allJobs.forEach(job => {
      // Exclude jobs that are finished (by status) or have no due date
      if (!job.date_due || job.status === 'Finish' || job.status === 'Complete') return;
      
      const dueDate = new Date(job.date_due);
      dueDate.setHours(0, 0, 0, 0);
      
      const diffTime = dueDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) {
        dueSoon.overdue.push(job);
      } else if (diffDays === 0) {
        dueSoon.today.push(job);
      } else if (diffDays === 1) {
        dueSoon.day1.push(job);
      } else if (diffDays === 2) {
        dueSoon.day2.push(job);
      } else if (diffDays === 3) {
        dueSoon.day3.push(job);
      } else if (diffDays === 4) {
        dueSoon.day4.push(job);
      } else if (diffDays === 5) {
        dueSoon.day5.push(job);
      }
    });
    
    return dueSoon;
  };

  const jobsDueSoon = getJobsDueSoon();

  // Approved Jobs — Ready to Schedule
  // ---------------------------------
  // Jobs the customer has already accepted (status = 'Approved') but that
  // haven't been given a `date_due` yet. These are the ones the user still
  // needs to book into the workshop schedule.
  //
  // For each job, we pull the *latest accepted* entry from `quote_history`
  // and use its `approval_date` as the "Approved X ago" timestamp — that's
  // exactly when the customer clicked the accept button on the remote
  // approval link. If for some legacy reason no accepted entry exists, we
  // fall back to `updated_at` and then `date_in` so the card never breaks.
  const getApprovedJobsToSchedule = () => {
    const list = [];
    for (const job of allJobs) {
      if (job.closed) continue;
      if (job.status !== 'Approved') continue;
      if (job.date_due) continue; // already scheduled

      // Latest accepted quote_history entry (most recent approval_date)
      let approvedAt = null;
      const history = Array.isArray(job.quote_history) ? job.quote_history : [];
      for (const q of history) {
        if (q?.approval_status === 'accepted' && q?.approval_date) {
          if (!approvedAt || new Date(q.approval_date) > new Date(approvedAt)) {
            approvedAt = q.approval_date;
          }
        }
      }
      // Fallback chain for older records without a proper approval_date
      const sortKey = approvedAt || job.updated_at || job.date_in || job.created_at;
      list.push({ job, approvedAt, sortKey });
    }
    list.sort((a, b) => new Date(b.sortKey) - new Date(a.sortKey));
    return list;
  };

  const approvedToSchedule = getApprovedJobsToSchedule();

  // "Approved 3 hours ago" style helper — kept local since it's only used
  // by this card and we don't want to introduce a new util for one caller.
  const formatApprovedAgo = (iso) => {
    if (!iso) return 'Approved recently';
    const then = new Date(iso);
    if (isNaN(then.getTime())) return 'Approved recently';
    const diffMs = Date.now() - then.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Approved just now';
    if (mins < 60) return `Approved ${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Approved ${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `Approved ${days} day${days === 1 ? '' : 's'} ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `Approved ${weeks} week${weeks === 1 ? '' : 's'} ago`;
    return `Approved ${formatDate(iso)}`;
  };

  const printSticker = (job) => printJobSticker(job, settings);

  const printFullForm = (job) => {
    if (!settings) return;
    // Smart Reminder — tell the user which printer to pick
    if (settings.printer_job_card) {
      toast.info(`🖨️ Send job card to: ${settings.printer_job_card}`, { duration: 4000 });
    }
    const bizInfo = settings.business_info;
    const terms = settings.terms_and_conditions;
    const formWindow = window.open('', '_blank', 'width=800,height=600');
    formWindow.document.write(`
      <html>
        <head>
          <title>Job Form - ${job.job_number}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              max-width: 800px;
              margin: 0 auto;
            }
            .business-header {
              text-align: center;
              border-bottom: 2px solid #333;
              padding-bottom: 15px;
              margin-bottom: 30px;
            }
            .business-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .business-details {
              font-size: 14px;
              color: #666;
            }
            .business-logo {
              max-width: 150px;
              max-height: 80px;
              margin-bottom: 10px;
            }
            h1 { text-align: center; margin-bottom: 30px; font-size: 20px; }
            .field { margin-bottom: 12px; }
            .label { font-weight: bold; display: inline-block; width: 180px; }
            .value { display: inline-block; }
            .terms {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #333;
              font-size: 11px;
              color: #666;
            }
            .terms-title {
              font-weight: bold;
              margin-bottom: 10px;
              font-size: 12px;
              color: #333;
            }
            .signature-area {
              margin-top: 40px;
              padding-top: 20px;
            }
            .signature-line {
              border-top: 2px solid #333;
              width: 300px;
              margin-top: 60px;
              padding-top: 10px;
              font-size: 12px;
            }
            .signature-image {
              max-width: 400px;
              max-height: 150px;
              border: 1px solid #ccc;
              margin-top: 10px;
              padding: 10px;
              background: white;
            }
            .signature-date {
              font-size: 11px;
              color: #666;
              margin-top: 5px;
            }
            .signed-badge {
              display: inline-block;
              background-color: #10b981;
              color: white;
              padding: 5px 15px;
              border-radius: 5px;
              font-size: 12px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            @media print {
              body { padding: 10px; }
            }
          </style>
        </head>
        <body>
          <div class="business-header">
            ${bizInfo.logo_url ? `<img src="${bizInfo.logo_url}" class="business-logo" alt="Logo" />` : ''}
            <div class="business-name">${bizInfo.business_name}</div>
            <div class="business-details">
              ${bizInfo.address ? `${bizInfo.address}<br/>` : ''}
              Phone: ${bizInfo.phone} | Email: ${bizInfo.email} | Website: ${bizInfo.website}
              ${bizInfo.abn ? `<br/>ABN: ${bizInfo.abn}` : ''}
            </div>
          </div>
          <h1>Job Booking Form</h1>
          <div class="field"><span class="label">Job Number:</span><span class="value">${job.job_number}</span></div>
          <div class="field"><span class="label">Customer Name:</span><span class="value">${job.customer_name}</span></div>
          <div class="field"><span class="label">Address:</span><span class="value">${job.address_line1}, ${job.suburb} ${job.state} ${job.postcode}</span></div>
          <div class="field"><span class="label">Email:</span><span class="value">${job.email}</span></div>
          <div class="field"><span class="label">Mobile:</span><span class="value">${job.mobile}</span></div>
          <div class="field"><span class="label">Status:</span><span class="value">${job.status}</span></div>
          <div class="field"><span class="label">Job Type:</span><span class="value">${job.job_type}</span></div>
          <div class="field"><span class="label">Brand:</span><span class="value">${job.brand}</span></div>
          <div class="field"><span class="label">Model Number:</span><span class="value">${job.model_number || 'N/A'}</span></div>
          <div class="field"><span class="label">Serial Number:</span><span class="value">${job.serial_number || 'N/A'}</span></div>
          <div class="field"><span class="label">Accessories:</span><span class="value">${Array.isArray(job.accessories) ? (job.accessories.length > 0 ? job.accessories.join(', ') : 'N/A') : (job.accessories || 'N/A')}</span></div>
          <div class="field"><span class="label">Job Description:</span><span class="value">${job.job_description || 'N/A'}</span></div>
          <div class="field"><span class="label">Date In:</span><span class="value">${formatDate(job.date_in)}</span></div>
          <div class="field"><span class="label">Date Out:</span><span class="value">${formatDate(job.date_out)}</span></div>
          <div class="field"><span class="label">Date Finished:</span><span class="value">${formatDate(job.date_finished)}</span></div>
          <div class="field"><span class="label">Date Due:</span><span class="value">${formatDate(job.date_due)}</span></div>
          <div class="field"><span class="label">Comments:</span><span class="value">${job.comments || 'N/A'}</span></div>
          <div class="field" style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd;"><span class="label">Total Amount (inc. GST):</span><span class="value">$${parseFloat(job.total_amount || 0).toFixed(2)}</span></div>
          <div class="field"><span class="label">GST %:</span><span class="value">${parseFloat(job.gst_percentage || 10).toFixed(2)}%</span></div>
          <div class="field"><span class="label">Subtotal (ex. GST):</span><span class="value">$${parseFloat(job.subtotal || 0).toFixed(2)}</span></div>
          <div class="field"><span class="label">GST Amount:</span><span class="value">$${parseFloat(job.gst_amount || 0).toFixed(2)}</span></div>
          <div class="terms">
            <div class="terms-title">Terms and Conditions:</div>
            <div>${terms}</div>
          </div>
          <div class="signature-area">
            <p style="font-size: 12px; margin-bottom: 10px;">By signing below, I acknowledge that I have read and agree to the above Terms and Conditions.</p>
            ${job.signature ? `
              <div class="signed-badge">✓ DIGITALLY SIGNED</div>
              <div>
                <strong>Customer Signature:</strong>
                <div class="signature-image">
                  <img src="${job.signature}" alt="Customer Signature" style="max-width: 100%; height: auto;" />
                </div>
                <div class="signature-date">
                  Signed on: ${job.signature_date ? new Date(job.signature_date).toLocaleString() : 'N/A'}
                </div>
              </div>
            ` : `
              <div class="signature-line">
                Customer Signature: _________________________________
              </div>
              <div style="margin-top: 10px; font-size: 11px; color: #666;">
                Date: _________________
              </div>
            `}
          </div>
        </body>
      </html>
    `);
    formWindow.document.close();
    setTimeout(() => formWindow.print(), 250);
  };

  return (
    <div className="dashboard-container">
      {/* Status Dashboard Cards */}
      {settings && (
        <div className="status-dashboard">
          <h2 className="dashboard-title">
            Job Status Overview
            <HelpTooltip 
              content="Click on any status card to filter jobs by that status. Click 'Total Jobs' to see all active jobs." 
              side="right"
            />
          </h2>
          <div className="status-cards">
            {settings.statuses.map((status) => (
              <Card 
                key={status} 
                className={`status-card ${selectedStatus === status ? 'status-card-selected' : ''}`} 
                data-testid={`status-card-${status.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => handleStatusClick(status)}
                style={{
                  borderLeft: `4px solid ${settings.status_colors?.[status] || '#6b7280'}`,
                  backgroundColor: selectedStatus === status ? `${settings.status_colors?.[status]}15` : undefined
                }}
              >
                <CardContent className="status-card-content">
                  <div className="status-card-label" style={{ color: settings.status_colors?.[status] || '#6b7280' }}>{status}</div>
                  <div className="status-card-count">{statusCounts[status] || 0}</div>
                  <div className="status-card-subtitle">
                    {statusCounts[status] === 1 ? 'job' : 'jobs'}
                  </div>
                  {selectedStatus === status && (
                    <div className="status-card-active-indicator" style={{ backgroundColor: settings.status_colors?.[status] || '#6b7280' }}>Filtered</div>
                  )}
                </CardContent>
              </Card>
            ))}
            {/* Feb 2026 — SMS Inbox card. Sits to the right of "Not
                Proceeding" in the status grid. Replaces the oversized
                "Ready to Schedule" footprint by claiming 2 columns and
                leaving Ready to Schedule with 3. Auto-hides when no SMS
                threads exist. */}
            <Card
              data-testid="sms-inbox-card"
              style={{
                borderLeft: `4px solid ${smsInboxTotalUnread > 0 ? '#dc2626' : '#6366f1'}`,
                background: smsInboxTotalUnread > 0
                  ? 'linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)'
                  : 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)',
                gridColumn: 'span 2',
                minWidth: 0,
              }}
            >
              <CardContent style={{ padding: '0.85rem 1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      💬 SMS Inbox
                    </span>
                    {smsInboxTotalUnread > 0 && (
                      <span
                        data-testid="sms-inbox-unread-badge"
                        style={{
                          backgroundColor: '#dc2626',
                          color: 'white',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          padding: '0.1rem 0.5rem',
                          borderRadius: '9999px',
                          minWidth: '1.5rem',
                          textAlign: 'center',
                        }}
                      >
                        {smsInboxTotalUnread}
                      </span>
                    )}
                  </div>
                  {smsNotifPermission !== 'granted' && typeof Notification !== 'undefined' && (
                    <button
                      onClick={requestSmsNotifPermission}
                      data-testid="sms-notif-enable-btn"
                      title="Enable browser notifications for new SMS replies"
                      style={{
                        fontSize: '0.65rem', background: 'transparent', border: '1px solid #c7d2fe',
                        padding: '2px 6px', borderRadius: 4, color: '#4338ca', cursor: 'pointer',
                      }}
                    >🔔 Enable alerts</button>
                  )}
                </div>
                {smsInboxThreads.length === 0 ? (
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', padding: '0.5rem 0' }}>
                    No SMS replies yet. New customer replies will appear here.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: '200px', overflowY: 'auto' }}>
                    {smsInboxThreads.slice(0, 6).map((thread) => (
                      <div
                        key={thread.job_id}
                        onClick={() => openSmsThread(thread.job_id)}
                        data-testid={`sms-inbox-thread-${thread.job_number}`}
                        title={`Click to open conversation with ${thread.customer_name}`}
                        style={{
                          padding: '0.4rem 0.5rem',
                          background: thread.unread_count > 0 ? '#ffffff' : 'rgba(255,255,255,0.5)',
                          border: `1px solid ${thread.unread_count > 0 ? '#fecaca' : '#e0e7ff'}`,
                          borderRadius: 4,
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.1rem',
                          transition: 'transform 100ms',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateX(2px)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateX(0)'; }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b' }}>
                            #{thread.job_number} · {thread.customer_name}
                          </span>
                          {thread.unread_count > 0 && (
                            <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#dc2626' }}>
                              ● {thread.unread_count} new
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {thread.latest_message}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Approved Jobs — Ready to Schedule.
                Placed inside the status grid so it fills the empty row-2 space
                to the right of "Not Proceeding" (spans up to 5 grid columns on
                wide viewports; wraps gracefully on narrower ones). Auto-hides
                when there's nothing pending. */}
            {approvedToSchedule.length > 0 && (
              <Card
                data-testid="approved-to-schedule-section"
                style={{
                  borderLeft: '4px solid #16a34a',
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
                  gridColumn: 'span 3',
                  minWidth: 0, // let the card shrink inside grid on narrow screens
                }}
              >
                <CardContent style={{ padding: '0.85rem 1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        ✅ Approved — Ready to Schedule
                      </span>
                      <HelpTooltip
                        content="Jobs the customer has accepted but that don't have a Date Due yet. Click a row to open the job and book it into the workshop schedule."
                        side="right"
                      />
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#15803d', fontWeight: 600 }}>
                      {approvedToSchedule.length > 5
                        ? `Showing 5 of ${approvedToSchedule.length}`
                        : `${approvedToSchedule.length} job${approvedToSchedule.length === 1 ? '' : 's'}`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    {approvedToSchedule.slice(0, 5).map(({ job, approvedAt }) => (
                      <div
                        key={job.id}
                        onClick={() => navigate(`/job/${job.id}`)}
                        data-testid={`approved-to-schedule-row-${job.id}`}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.4rem 0.65rem',
                          background: 'white',
                          border: '1px solid #d1fae5',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s, border-color 0.15s',
                          gap: '0.5rem',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f0fdf4';
                          e.currentTarget.style.borderColor = '#86efac';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.borderColor = '#d1fae5';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', minWidth: 0, flex: 1 }}>
                          <span style={{ fontWeight: 700, color: '#2563eb', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                            #{job.job_number}
                          </span>
                          <span style={{ color: '#1f2937', fontSize: '0.85rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {job.customer_name}
                          </span>
                          {typeof job.total_amount === 'number' && job.total_amount > 0 && (
                            <span style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              ${parseFloat(job.total_amount).toFixed(2)}
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: '0.72rem', color: '#4b5563', fontStyle: 'italic', whiteSpace: 'nowrap', marginLeft: '0.5rem' }}>
                          {formatApprovedAgo(approvedAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          <div className="dashboard-summary-row">
          <div className="total-jobs-card">
            <Card 
              onClick={clearFilters} 
              className={selectedStatus ? '' : 'total-card-selected'}
              style={{
                cursor: 'pointer',
                background: settings?.dashboard_total_jobs_color || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none'
              }}
            >
              <CardContent className="total-jobs-content">
                <div className="total-jobs-label">Total Jobs</div>
                <div className="total-jobs-count">{allJobs.length}</div>
                {!selectedStatus && <div className="status-card-active-indicator">Showing All</div>}
              </CardContent>
            </Card>
          </div>
          
          {/* Booked (provisional) purchases/expenses needing follow-up */}
          {bookedStale.count > 0 && (
            <div data-testid="booked-stale-banner" style={{ margin: '0.75rem 0', padding: '0.75rem 1rem', background: '#e0e7ff', border: '1px solid #818cf8', borderRadius: 6, display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <div style={{ fontSize: '1.25rem' }}>📌</div>
              <div style={{ flex: 1, fontSize: '0.85rem' }}>
                <div style={{ fontWeight: 600, color: '#3730a3' }}>
                  {bookedStale.count} provisional transaction{bookedStale.count > 1 ? 's' : ''} still Booked after {bookedStale.threshold_days} days
                </div>
                {bookedStale.stale_purchases.length > 0 && (
                  <div style={{ color: '#4338ca', marginTop: '0.25rem' }}>
                    <strong>{bookedStale.stale_purchases.length} purchase{bookedStale.stale_purchases.length > 1 ? 's' : ''}:</strong>{' '}
                    {bookedStale.stale_purchases.slice(0, 3).map(p => `${p.supplier_name || 'Unknown'} (${p.days_old}d, $${(p.total_cost || 0).toFixed(2)})`).join(', ')}
                    {bookedStale.stale_purchases.length > 3 && ` +${bookedStale.stale_purchases.length - 3} more`}
                  </div>
                )}
                {bookedStale.stale_expenses.length > 0 && (
                  <div style={{ color: '#4338ca', marginTop: '0.25rem' }}>
                    <strong>{bookedStale.stale_expenses.length} expense{bookedStale.stale_expenses.length > 1 ? 's' : ''}:</strong>{' '}
                    {bookedStale.stale_expenses.slice(0, 3).map(e => `${e.supplier_name || e.description || 'Unknown'} (${e.days_old}d, $${(e.total_amount || 0).toFixed(2)})`).join(', ')}
                    {bookedStale.stale_expenses.length > 3 && ` +${bookedStale.stale_expenses.length - 3} more`}
                  </div>
                )}
                <div style={{ color: '#64748b', marginTop: '0.35rem', fontSize: '0.75rem' }}>
                  Open Purchases or Expenses and change status to Finished once the transaction has cleared, or delete if cancelled.
                </div>
              </div>
            </div>
          )}

          {/* Awaiting Payment Card */}
          <div className="awaiting-payment-card">
            <Card 
              onClick={() => {
                // "Awaiting Payment" = physical goods on shelf, done, waiting
                // for customer to collect + pay. Precise definition matches the
                // Reports Payment Status derivation (Jul 2026 refactor):
                //   status ∈ {Finish, Not Recoverable, Not Proceeding}
                //   AND revenue_date IS NULL (not yet closed out)
                //   AND balance_owing > 0
                const AWAIT_STATUSES = ['Finish', 'Not Recoverable', 'Not Proceeding'];
                const awaitingPaymentJobs = allJobs.filter(job => {
                  const balanceOwing = job.balance_owing !== undefined ? job.balance_owing : (job.total_amount || 0);
                  return AWAIT_STATUSES.includes(job.status)
                    && !job.revenue_date
                    && balanceOwing > 0;
                });
                setJobs(awaitingPaymentJobs);
                setSelectedPaymentStatus('Awaiting');
                setSelectedStatus(null);
              }}
              className={selectedPaymentStatus === 'Awaiting' ? 'awaiting-card-selected' : ''}
              style={{ 
                cursor: 'pointer',
                background: `linear-gradient(135deg, ${settings?.awaiting_payment_color || '#eab308'} 0%, ${settings?.awaiting_payment_color ? `${settings.awaiting_payment_color}dd` : '#ca8a04'} 100%)`,
                color: 'white',
                border: 'none'
              }}
            >
              <CardContent className="awaiting-payment-content">
                <div className="awaiting-payment-label" style={{ color: 'white', fontWeight: '600' }}>
                  ⏳ Awaiting Payment
                  <HelpTooltip 
                    content="Jobs that are done and on your shelf waiting for the customer to collect and pay. Status is Finish / Not Recoverable / Not Proceeding, no revenue_date yet, balance owing. Click to see the chase list." 
                    side="right"
                  />
                </div>
                <div className="awaiting-payment-count" style={{ color: 'white', fontSize: '1.75rem', fontWeight: 'bold', margin: '0.1rem 0' }}>
                  {allJobs.filter(job => {
                    const AWAIT_STATUSES = ['Finish', 'Not Recoverable', 'Not Proceeding'];
                    const balanceOwing = job.balance_owing !== undefined ? job.balance_owing : (job.total_amount || 0);
                    return AWAIT_STATUSES.includes(job.status) && !job.revenue_date && balanceOwing > 0;
                  }).length} Jobs
                </div>
                <div className="awaiting-payment-amount" style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold' }}>
                  ${allJobs
                    .filter(job => {
                      const AWAIT_STATUSES = ['Finish', 'Not Recoverable', 'Not Proceeding'];
                      const balanceOwing = job.balance_owing !== undefined ? job.balance_owing : (job.total_amount || 0);
                      return AWAIT_STATUSES.includes(job.status) && !job.revenue_date && balanceOwing > 0;
                    })
                    .reduce((sum, job) => {
                      const balanceOwing = job.balance_owing !== undefined ? job.balance_owing : (job.total_amount || 0);
                      return sum + balanceOwing;
                    }, 0)
                    .toFixed(2)
                  }
                </div>
                {selectedPaymentStatus === 'Awaiting' && (
                  <div className="status-card-active-indicator" style={{ color: 'white' }}>Filtered</div>
                )}
              </CardContent>
            </Card>
          </div>
          </div>
        </div>
      )}

      {/* In Progress / Workshop Status — operational sub-status independent of customer-facing Status */}
      {settings && (
        <div className="status-dashboard" style={{ marginTop: '1.5rem' }} data-testid="in-progress-section">
          <h2 className="dashboard-title">
            In Progress
            <HelpTooltip
              content="Operational workshop status — tracks what phase active jobs are actually in, independent of the customer-facing Status. Click a card to filter."
              side="right"
            />
          </h2>
          <div className="status-cards">
            {[
              { key: 'Not Started',     color: '#94a3b8', icon: '⏸' },
              { key: 'Imaging',         color: '#0ea5e9', icon: '💾' },
              { key: 'WIP',             color: '#f59e0b', icon: '🛠' },
              { key: 'Awaiting Parts',  color: '#ef4444', icon: '📦' },
            ].map(({ key, color, icon }) => {
              const count = allJobs.filter(j => !j.closed && (j.progress_status || 'Not Started') === key).length;
              const isSelected = selectedProgressStatus === key;
              return (
                <Card
                  key={key}
                  className={`status-card ${isSelected ? 'status-card-selected' : ''}`}
                  data-testid={`progress-status-card-${key.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => handleProgressStatusClick(key)}
                  style={{
                    borderLeft: `4px solid ${color}`,
                    backgroundColor: isSelected ? `${color}18` : undefined,
                    cursor: 'pointer',
                  }}
                >
                  <CardContent className="status-card-content">
                    <div className="status-card-label" style={{ color }}>
                      <span style={{ marginRight: '0.35rem' }}>{icon}</span>{key}
                    </div>
                    <div className="status-card-count">{count}</div>
                    <div className="status-card-subtitle">{count === 1 ? 'job' : 'jobs'}</div>
                    {isSelected && (
                      <div className="status-card-active-indicator" style={{ backgroundColor: color }}>Filtered</div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Jobs Due Soon Section */}
      {settings && (jobsDueSoon.overdue.length > 0 || jobsDueSoon.today.length > 0 || 
                    jobsDueSoon.day1.length > 0 || jobsDueSoon.day2.length > 0 || 
                    jobsDueSoon.day3.length > 0 || jobsDueSoon.day4.length > 0 || 
                    jobsDueSoon.day5.length > 0) && (
        <div className="due-soon-section">
          <Card>
            <CardHeader>
              <CardTitle>Jobs Due Soon</CardTitle>
              <CardDescription>Track upcoming job deadlines</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="due-soon-grid">
                {[
                  { key: 'overdue', label: 'Overdue',   icon: '⚠️', cls: 'due-overdue', jobs: jobsDueSoon.overdue },
                  { key: 'today',   label: 'Due Today', icon: '🔴', cls: 'due-today',   jobs: jobsDueSoon.today   },
                  { key: 'day1',    label: 'Tomorrow',  icon: '🟠', cls: 'due-day1',    jobs: jobsDueSoon.day1    },
                  { key: 'day2',    label: 'In 2 Days', icon: '🟡', cls: 'due-day2',    jobs: jobsDueSoon.day2    },
                  { key: 'day3',    label: 'In 3 Days', icon: '🟢', cls: 'due-day3',    jobs: jobsDueSoon.day3    },
                  { key: 'day4',    label: 'In 4 Days', icon: '🔵', cls: 'due-day4',    jobs: jobsDueSoon.day4    },
                  { key: 'day5',    label: 'In 5 Days', icon: '🟣', cls: 'due-day5',    jobs: jobsDueSoon.day5    },
                ].filter(b => b.jobs && b.jobs.length > 0).map(b => (
                  <div key={b.key} className={`due-card ${b.cls}`} data-testid={`due-${b.key}`}>
                    <div
                      className="due-card-header"
                      onClick={() => openDueBucket(b.label, b.icon, b.jobs)}
                      style={{ cursor: 'pointer' }}
                      title={`View all ${b.jobs.length} ${b.label.toLowerCase()} jobs`}
                      data-testid={`due-${b.key}-header`}
                    >
                      <span className="due-card-icon">{b.icon}</span>
                      <span className="due-card-title">{b.label}</span>
                    </div>
                    <div
                      className="due-card-count"
                      onClick={() => openDueBucket(b.label, b.icon, b.jobs)}
                      style={{ cursor: 'pointer' }}
                      data-testid={`due-${b.key}-count`}
                    >
                      {b.jobs.length}
                    </div>
                    <div className="due-card-jobs">
                      {b.jobs.slice(0, 3).map(job => (
                        <div key={job.id} className="due-job-item" onClick={() => navigate(`/job/${job.id}`)}>
                          <span className="due-job-number">#{job.job_number}</span>
                          <span className="due-job-customer">{job.customer_name}</span>
                          <span className="due-job-date">{formatDate(job.date_due)}</span>
                        </div>
                      ))}
                      {b.jobs.length > 3 && (
                        <div
                          className="due-job-more"
                          onClick={() => openDueBucket(b.label, b.icon, b.jobs)}
                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                          data-testid={`due-${b.key}-more`}
                        >
                          +{b.jobs.length - 3} more — click to view all
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reminders Section */}
      {(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Get ALL job-related reminders (not completed) - including future ones
        const jobReminders = allJobs.filter(job => 
          !job.closed && job.comments && job.comments.some(c => c.follow_up_date && !c.completed)
        ).flatMap(job => 
          job.comments
            .map((comment, index) => ({ ...comment, job, commentIndex: index }))
            .filter(c => c.follow_up_date && !c.completed)
            .map(c => {
              const followUpDate = new Date(c.follow_up_date);
              followUpDate.setHours(0, 0, 0, 0);
              const daysDiff = Math.floor((today - followUpDate) / (1000 * 60 * 60 * 24));
              return {
                ...c,
                type: 'job',
                followUpDate,
                daysOverdue: daysDiff,
                daysUntil: -daysDiff
              };
            })
        );

        // Process general reminders
        const processedGeneralReminders = generalReminders.map(r => {
          const followUpDate = new Date(r.due_date);
          followUpDate.setHours(0, 0, 0, 0);
          const daysDiff = Math.floor((today - followUpDate) / (1000 * 60 * 60 * 24));
          return {
            ...r,
            type: 'general',
            followUpDate,
            daysOverdue: daysDiff,
            daysUntil: -daysDiff
          };
        });

        // Combine all reminders
        const allReminders = [...jobReminders, ...processedGeneralReminders];

        const overdueReminders = allReminders.filter(r => r.daysOverdue > 0);
        const todayReminders = allReminders.filter(r => r.daysOverdue === 0);
        const upcomingReminders = allReminders.filter(r => r.daysOverdue < 0).sort((a, b) => a.daysUntil - b.daysUntil);
        
        return (
          <div className="reminders-section" style={{ marginTop: '2rem' }}>
            <Card>
              <CardHeader>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <CardTitle>
                      Reminders
                      <HelpTooltip 
                        content="View all your reminders here. Job reminders are linked to specific jobs, while general reminders are standalone tasks. Click any card to see all reminders." 
                        side="right"
                      />
                    </CardTitle>
                    <CardDescription>Follow-up reminders that need attention</CardDescription>
                  </div>
                  <Button 
                    onClick={() => setShowReminderModal(true)}
                    data-testid="set-reminder-btn"
                    style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}
                  >
                    + Set Reminder
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                  {/* Overdue Reminders Card - Customizable color (default red) */}
                  <Card 
                    style={{ 
                      background: `linear-gradient(135deg, ${settings?.reminder_color || '#dc2626'} 0%, ${settings?.reminder_color ? `${settings.reminder_color}dd` : '#b91c1c'} 100%)`,
                      color: 'white',
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate('/reminders')}
                    data-testid="overdue-reminders-card"
                  >
                    <CardContent style={{ padding: '1.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>
                            ⚠️ Overdue
                          </div>
                          <div style={{ fontSize: '2rem', fontWeight: '700' }}>
                            {overdueReminders.length}
                          </div>
                          <div style={{ fontSize: '0.875rem', marginTop: '0.5rem', opacity: 0.9 }}>
                            {overdueReminders.length === 0 ? (
                              'No overdue reminders'
                            ) : (
                              <>
                                {overdueReminders.slice(0, 2).map((r, i) => (
                                  <div key={i} style={{ fontSize: '0.9rem', marginTop: '0.25rem', fontWeight: '500' }}>
                                    {r.type === 'job' ? `#${r.job.job_number}` : '📌'} - {r.daysOverdue} day{r.daysOverdue > 1 ? 's' : ''} overdue
                                  </div>
                                ))}
                                {overdueReminders.length > 2 && (
                                  <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                                    +{overdueReminders.length - 2} more
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>
                          🔔
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Due Today Reminders Card - Customizable color (default orange) */}
                  <Card 
                    style={{ 
                      background: `linear-gradient(135deg, ${settings?.due_today_color || '#ea580c'} 0%, ${settings?.due_today_color ? `${settings.due_today_color}dd` : '#c2410c'} 100%)`,
                      color: 'white',
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate('/reminders')}
                    data-testid="due-today-reminders-card"
                  >
                    <CardContent style={{ padding: '1.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>
                            📅 Due Today
                          </div>
                          <div style={{ fontSize: '2rem', fontWeight: '700' }}>
                            {todayReminders.length}
                          </div>
                          <div style={{ fontSize: '0.875rem', marginTop: '0.5rem', opacity: 0.9 }}>
                            {todayReminders.length === 0 ? (
                              'No reminders due today'
                            ) : (
                              <>
                                {todayReminders.slice(0, 2).map((r, i) => (
                                  <div key={i} style={{ fontSize: '0.9rem', marginTop: '0.25rem', fontWeight: '500' }}>
                                    {r.type === 'job' ? `#${r.job.job_number} - ${r.job.customer_name}` : `📌 ${r.title}`}
                                  </div>
                                ))}
                                {todayReminders.length > 2 && (
                                  <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                                    +{todayReminders.length - 2} more
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>
                          🔔
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Upcoming Reminders Card - Blue/teal color */}
                  <Card 
                    style={{ 
                      background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate('/reminders')}
                    data-testid="upcoming-reminders-card"
                  >
                    <CardContent style={{ padding: '1.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>
                            🗓️ Upcoming
                          </div>
                          <div style={{ fontSize: '2rem', fontWeight: '700' }}>
                            {upcomingReminders.length}
                          </div>
                          <div style={{ fontSize: '0.875rem', marginTop: '0.5rem', opacity: 0.9 }}>
                            {upcomingReminders.length === 0 ? (
                              'No upcoming reminders'
                            ) : (
                              <>
                                {upcomingReminders.slice(0, 2).map((r, i) => (
                                  <div key={i} style={{ fontSize: '0.9rem', marginTop: '0.25rem', fontWeight: '500' }}>
                                    {r.type === 'job' ? `#${r.job.job_number}` : `📌 ${r.title.substring(0, 15)}${r.title.length > 15 ? '...' : ''}`} - in {r.daysUntil} day{r.daysUntil > 1 ? 's' : ''}
                                  </div>
                                ))}
                                {upcomingReminders.length > 2 && (
                                  <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                                    +{upcomingReminders.length - 2} more
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>
                          🔔
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <div style={{ fontSize: '0.75rem', marginTop: '1rem', color: '#6b7280', textAlign: 'center' }}>
                  Click any card to view all reminders
                </div>
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Dashboard Analytics Section */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '2rem' }}>
        {/* Revenue Chart */}
        <Card>
          <CardHeader className="pb-2">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div>
                <CardTitle style={{ fontSize: '1rem' }}>
                  Revenue Overview
                  <HelpTooltip 
                    content="Shows revenue from completed jobs only (excluding GST). Use the dropdown to change the time period or select custom dates." 
                    side="right"
                  />
                </CardTitle>
                <CardDescription>Revenue received (inc GST, cash-basis)</CardDescription>
              </div>
              <Select value={revenuePeriod} onValueChange={handleRevenuePeriodChange}>
                <SelectTrigger style={{ width: '130px' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Last 7 Days</SelectItem>
                  <SelectItem value="monthly">Last 30 Days</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {revenuePeriod === 'custom' && (
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <Input
                  type="date"
                  value={revenueStartDate}
                  onChange={(e) => setRevenueStartDate(e.target.value)}
                  style={{ width: '140px' }}
                  data-testid="revenue-start-date"
                />
                <span style={{ color: '#6b7280' }}>to</span>
                <Input
                  type="date"
                  value={revenueEndDate}
                  onChange={(e) => setRevenueEndDate(e.target.value)}
                  style={{ width: '140px' }}
                  data-testid="revenue-end-date"
                />
                <Button size="sm" onClick={handleRevenueCustomDates} data-testid="revenue-apply-dates">
                  Apply
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <div style={{ display: 'flex', gap: '2rem', marginBottom: '1rem' }}>
              <div
                onClick={() => openRevenueJobsModal()}
                style={{ cursor: 'pointer', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', transition: 'background 0.15s' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                title="Click to see the jobs in this revenue"
                data-testid="revenue-total-clickable"
              >
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Total Revenue <span style={{ fontSize: '0.7rem', color: '#16a34a' }}>(click to view jobs)</span></div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: settings?.dashboard_revenue_color || '#16a34a' }}>
                  ${revenueData.total_revenue.toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div
                onClick={() => openRevenueJobsModal()}
                style={{ cursor: 'pointer', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', transition: 'background 0.15s' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#eff6ff')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                title="Click to see the jobs in this period"
                data-testid="revenue-jobs-clickable"
              >
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Jobs Completed <span style={{ fontSize: '0.7rem', color: '#2563eb' }}>(click to view)</span></div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: settings?.dashboard_jobs_completed_color || '#2563eb' }}>
                  {revenueData.total_jobs}
                </div>
              </div>
            </div>
            <div style={{ height: '200px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueData.data}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis fontSize={11} tickLine={false} axisLine={false} tickFormatter={(value) => `$${value}`} />
                  <RechartsTooltip 
                    formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']}
                    labelFormatter={(label) => label}
                    contentStyle={{ fontSize: '12px' }}
                  />
                  <Bar 
                    dataKey="revenue" 
                    fill={settings?.dashboard_revenue_color || '#16a34a'} 
                    radius={[4, 4, 0, 0]} 
                    cursor="pointer"
                    onClick={(data) => {
                      // Each bucket carries `day_key` (YYYY-MM-DD) from the
                      // backend so a click drills into that specific day only.
                      // Fallback: if the bucket has no day_key (legacy) or no
                      // revenue, do nothing.
                      if (data && data.revenue > 0 && data.day_key) {
                        openRevenueJobsModal(data.day_key);
                      }
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent Jobs */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle style={{ fontSize: '1rem' }}>Recent Activity</CardTitle>
            <CardDescription>Last {recentJobs.length} updated jobs</CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
              {recentJobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  No recent activity
                </div>
              ) : (
                recentJobs.map((job) => (
                  <div 
                    key={job.id}
                    onClick={() => navigate(`/job/${job.id}`)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.75rem',
                      borderBottom: '1px solid #f3f4f6',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    data-testid={`recent-job-${job.id}`}
                  >
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                        <span style={{ color: '#2563eb' }}>#{job.job_number}</span>
                        <span style={{ marginLeft: '0.5rem', color: '#1f2937' }}>{job.customer_name}</span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {job.updated_at ? `Updated ${formatDate(job.updated_at)}` : `Created ${formatDate(job.date_in)}`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {job.total_amount > 0 && (
                        <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#16a34a' }}>
                          ${parseFloat(job.total_amount).toFixed(2)}
                        </span>
                      )}
                      {/* Progress Status pill — only shown when actively "in flight".
                          Colors match the Dashboard operational tiles so the eye
                          learns the mapping (blue = Imaging, amber = WIP, red =
                          Awaiting Parts). "Not Started" is intentionally omitted
                          to avoid visual noise on brand-new / freshly-booked jobs. */}
                      {(() => {
                        const ps = job.progress_status;
                        if (!ps || ps === 'Not Started') return null;
                        const styles = {
                          'Imaging':        { bg: '#e0f2fe', fg: '#075985', icon: '💾' },
                          'WIP':            { bg: '#fef3c7', fg: '#92400e', icon: '🛠' },
                          'Awaiting Parts': { bg: '#fee2e2', fg: '#991b1b', icon: '📦' },
                        }[ps] || { bg: '#f1f5f9', fg: '#334155', icon: '' };
                        return (
                          <span
                            title={`Job progress: ${ps}`}
                            data-testid={`recent-job-progress-${ps.toLowerCase().replace(/\s+/g, '-')}`}
                            style={{
                              fontSize: '0.7rem',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '9999px',
                              backgroundColor: styles.bg,
                              color: styles.fg,
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {styles.icon} {ps}
                          </span>
                        );
                      })()}
                      <span style={{
                        fontSize: '0.7rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '9999px',
                        backgroundColor: job.status === 'Completed' ? '#dcfce7' : 
                                        job.status === 'In Progress' ? '#dbeafe' : 
                                        job.status === 'Quote' ? '#fef3c7' : '#f3f4f6',
                        color: job.status === 'Completed' ? '#166534' : 
                               job.status === 'In Progress' ? '#1e40af' : 
                               job.status === 'Quote' ? '#92400e' : '#374151'
                      }}>
                        {job.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
              <Button variant="ghost" size="sm" onClick={() => navigate('/jobs')} style={{ fontSize: '0.75rem' }}>
                View All Jobs →
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Global Search Hint */}
      <div style={{ 
        marginTop: '1rem', 
        padding: '0.75rem 1rem', 
        backgroundColor: '#f0f9ff', 
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '0.75rem',
        fontSize: '0.875rem',
        color: '#0369a1'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>💡</span>
          <span>Press <kbd style={{ padding: '0.15rem 0.4rem', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #bae6fd', fontFamily: 'monospace' }}>Ctrl</kbd> + <kbd style={{ padding: '0.15rem 0.4rem', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #bae6fd', fontFamily: 'monospace' }}>K</kbd> to quickly search across jobs, customers, and suppliers</span>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => navigate('/help')}
          style={{ borderColor: '#bae6fd', color: '#0369a1' }}
          data-testid="dashboard-help-btn"
        >
          📖 User Guide
        </Button>
      </div>

      <div className="search-section">
        <div className="filter-row">
          {selectedStatus && (
            <div className="filter-indicator">
              <span>Status: <strong>{selectedStatus}</strong></span>
              <Button size="sm" variant="ghost" onClick={() => {setSelectedStatus(null); fetchJobs();}}>✕</Button>
            </div>
          )}
          {selectedPaymentStatus && (
            <div className="filter-indicator">
              <span>Payment: <strong>{selectedPaymentStatus}</strong></span>
              <Button size="sm" variant="ghost" onClick={() => {setSelectedPaymentStatus(null); fetchJobs();}}>✕</Button>
            </div>
          )}
        </div>
        
        <div className="search-controls">
          <Input
            type="text"
            placeholder="Search by name, mobile, email, or job number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            data-testid="search-input"
            style={{ flex: 1 }}
          />
          <Select
            value={selectedPaymentStatus || 'all'}
            onValueChange={(value) => {
              const paymentStatus = value === 'all' ? null : value;
              setSelectedPaymentStatus(paymentStatus);
              if (paymentStatus) {
                let filtered = allJobs;
                if (selectedStatus) filtered = filtered.filter(j => j.status === selectedStatus);
                setJobs(filtered.filter(j => (j.payment_status || 'Unpaid') === paymentStatus));
              } else {
                fetchJobs();
              }
            }}
          >
            <SelectTrigger style={{ width: '180px' }}>
              <SelectValue placeholder="Payment Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payments</SelectItem>
              <SelectItem value="Paid">Paid</SelectItem>
              <SelectItem value="Partial">Partial</SelectItem>
              <SelectItem value="Unpaid">Unpaid</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleSearch} data-testid="search-button">Search</Button>
          <Popover open={presetPopoverOpen} onOpenChange={setPresetPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="filter-presets-btn"
                title="Save and re-apply named filter combinations"
              >
                ⭐ Presets{filterPresets.length > 0 ? ` (${filterPresets.length})` : ''}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" style={{ width: '360px', padding: '0.75rem' }}>
              {/* Save current — top block. Shows a live summary of what's about
                  to be saved so the user knows exactly what the preset captures. */}
              <div style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: '0.35rem' }}>
                  💾 Save current filters as preset
                </div>
                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.5rem', fontStyle: 'italic' }} data-testid="filter-preset-current-summary">
                  {summarizePreset({
                    status: selectedStatus,
                    payment_status: selectedPaymentStatus,
                    progress: selectedProgressStatus,
                    chip: paymentFlagFilter !== 'all' ? paymentFlagFilter : null,
                    q: (searchTerm && searchTerm.trim()) || null,
                  })}
                </div>
                <div style={{ display: 'flex', gap: '0.35rem' }}>
                  <Input
                    type="text"
                    placeholder="Preset name (e.g. Ready to hand back)"
                    value={newPresetName}
                    onChange={(e) => setNewPresetName(e.target.value)}
                    onKeyPress={(e) => { if (e.key === 'Enter') saveCurrentAsPreset(); }}
                    data-testid="filter-preset-name-input"
                    disabled={!hasAnyActiveFilter}
                    style={{ flex: 1, fontSize: '0.85rem' }}
                  />
                  <Button
                    size="sm"
                    onClick={saveCurrentAsPreset}
                    disabled={!hasAnyActiveFilter || !newPresetName.trim()}
                    data-testid="filter-preset-save-btn"
                  >
                    Save
                  </Button>
                </div>
                {!hasAnyActiveFilter && (
                  <div style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '0.35rem' }}>
                    Apply at least one filter before saving.
                  </div>
                )}
              </div>

              {/* Saved list */}
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: '0.35rem' }}>
                📋 Saved presets
              </div>
              {filterPresets.length === 0 ? (
                <div style={{ fontSize: '0.75rem', color: '#6b7280', padding: '0.5rem', textAlign: 'center' }} data-testid="filter-preset-empty">
                  No saved presets yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: '260px', overflowY: 'auto' }}>
                  {filterPresets.map((p) => (
                    <div
                      key={p.id}
                      data-testid={`filter-preset-row-${p.id}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.4rem 0.5rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        transition: 'background-color 0.12s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <div
                        onClick={() => applyPreset(p)}
                        style={{ flex: 1, minWidth: 0 }}
                        data-testid={`filter-preset-apply-${p.id}`}
                      >
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.name}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {summarizePreset(p.params)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); deletePreset(p.id); }}
                        title="Delete preset"
                        data-testid={`filter-preset-delete-${p.id}`}
                        style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1rem', padding: '0.15rem 0.35rem', marginLeft: '0.35rem' }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </PopoverContent>
          </Popover>
          <Button onClick={clearFilters} variant="outline" data-testid="clear-search-button">Clear All</Button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading jobs...</div>
      ) : jobs.length === 0 ? (
        <div className="empty-state">
          <p>No jobs found. Create your first job!</p>
        </div>
      ) : (
        <div className="jobs-table-container">
          {/* Jul 2026 — Payment-flag chip row. Sits directly above the table so
              it's spatially grouped with "how you filter this list". Compose
              on top of the status card / search / payment-status dropdown.
              Each chip shows a live count next to its label so you don't need
              to click to know the size of each bucket. */}
          <div
            data-testid="payment-chip-row"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 0.5rem',
              marginBottom: '0.75rem',
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, marginRight: '0.25rem' }}>
              Show only:
            </span>
            {paymentChipDefs.map((chip) => {
              const isActive = paymentFlagFilter === chip.key;
              const count = chip.key === 'all' ? jobs.length : jobs.filter(chip.test).length;
              // Feb 2026 — active-state colours are colocated on the chip
              // def itself (activeBg/activeColor/activeBorder). Structural
              // guarantee: a chip cannot be added without its palette,
              // eliminating the earlier lookup-miss crash class.
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setPaymentFlagFilter(isActive && chip.key !== 'all' ? 'all' : chip.key)}
                  data-testid={`payment-chip-${chip.key}`}
                  data-active={isActive}
                  style={{
                    padding: '0.3rem 0.85rem',
                    borderRadius: '9999px',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
                    background: isActive ? chip.activeBg : '#ffffff',
                    color: isActive ? chip.activeColor : '#4b5563',
                    border: `1px solid ${isActive ? chip.activeBorder : '#d1d5db'}`,
                  }}
                >
                  {chip.label}
                  <span style={{
                    marginLeft: '0.35rem',
                    padding: '0.05rem 0.4rem',
                    borderRadius: '9999px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    background: isActive ? 'rgba(255,255,255,0.22)' : '#f3f4f6',
                    color: isActive ? '#ffffff' : '#6b7280',
                  }}>
                    {count}
                  </span>
                </button>
              );
            })}
            {paymentFlagFilter !== 'all' && (
              <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: 'auto' }} data-testid="chip-filter-count">
                Showing {visibleJobs.length} of {jobs.length}
              </span>
            )}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Job Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead title="Days since job was finished — for jobs with a balance owing">Days Owed</TableHead>
                <TableHead>Date In</TableHead>
                <TableHead>Close</TableHead>
                <TableHead style={{ minWidth: '220px' }}>
                  <div style={{ display: 'flex', gap: '10px', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: '#64748b', whiteSpace: 'nowrap' }}>
                    <span title="Print sticker">Sticker</span>
                    <span title="Print full job form">Print</span>
                    <span title="Mark customer as contacted">Call</span>
                    <span title="Email delivery/open status via Brevo">Emails</span>
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleJobs.map((job) => (
                <TableRow key={job.id} data-testid={`job-row-${job.job_number}`}>
                  <TableCell className="font-semibold">
                    <button
                      type="button"
                      onClick={() => navigate(`/job/${job.id}`)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: '#2563eb',
                        cursor: 'pointer',
                        font: 'inherit',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px'
                      }}
                      title="Open job details"
                      data-testid={`job-number-link-${job.job_number}`}
                    >
                      {job.job_number}
                    </button>
                  </TableCell>
                  <TableCell>
                    {/* Jul 2026 — customer name + at-a-glance money-in pills.
                        💵 Deposit  — job.deposit_received === true (blue)
                        💰 Paid     — job.payment_received === true (green)
                        Both auto-hide when the flag is false so quiet rows
                        stay quiet. Hover a pill for the exact timestamp +
                        who marked it (matches the JobDetail button UX 1:1). */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <span>{job.customer_name}</span>
                      {job.deposit_received && (
                        <span
                          data-testid={`job-deposit-pill-${job.job_number}`}
                          title={`Deposit received${job.deposit_received_at ? ` on ${new Date(job.deposit_received_at).toLocaleString('en-AU')}` : ''}${job.deposit_received_by ? ` by ${job.deposit_received_by}` : ''}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.15rem',
                            padding: '0.1rem 0.5rem',
                            borderRadius: '9999px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            backgroundColor: '#dbeafe',
                            color: '#1e40af',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.5,
                          }}
                        >
                          💵 Deposit
                        </span>
                      )}
                      {job.payment_received && (
                        <span
                          data-testid={`job-paid-pill-${job.job_number}`}
                          title={`Payment received${job.payment_received_at ? ` on ${new Date(job.payment_received_at).toLocaleString('en-AU')}` : ''}${job.payment_received_by ? ` by ${job.payment_received_by}` : ''}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.15rem',
                            padding: '0.1rem 0.5rem',
                            borderRadius: '9999px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            backgroundColor: '#d1fae5',
                            color: '#065f46',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.5,
                          }}
                        >
                          💰 Paid
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{job.mobile}</TableCell>
                  <TableCell>{job.job_type}</TableCell>
                  <TableCell>
                    <Popover
                      open={statusPopoverJobId === job.id}
                      onOpenChange={(open) => setStatusPopoverJobId(open ? job.id : null)}
                    >
                      <PopoverTrigger asChild>
                        <span
                          data-testid={`status-pill-${job.job_number}`}
                          title="Click to change status"
                          style={{
                            display: 'inline-block',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            backgroundColor: settings?.status_colors?.[job.status] || '#6b7280',
                            color: '#fff',
                            cursor: 'pointer',
                            opacity: statusSavingJobId === job.id ? 0.6 : 1,
                            transition: 'opacity 150ms',
                            userSelect: 'none',
                          }}
                        >
                          {job.status}
                        </span>
                      </PopoverTrigger>
                      <PopoverContent align="start" style={{ width: '12rem', padding: '0.4rem' }}>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', padding: '0.25rem 0.5rem 0.4rem', fontWeight: 600, letterSpacing: '0.03em' }}>
                          CHANGE STATUS
                        </div>
                        {(settings?.statuses || []).map((st) => {
                          const isCurrent = st === job.status;
                          return (
                            <button
                              key={st}
                              type="button"
                              data-testid={`inline-status-option-${job.job_number}-${st.replace(/\s+/g, '-').toLowerCase()}`}
                              onClick={() => inlineUpdateStatus(job, st)}
                              disabled={statusSavingJobId === job.id}
                              style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.4rem 0.5rem',
                                border: 'none',
                                background: isCurrent ? '#f1f5f9' : 'transparent',
                                borderRadius: '4px',
                                cursor: statusSavingJobId === job.id ? 'wait' : 'pointer',
                                fontSize: '0.85rem',
                                textAlign: 'left',
                                fontWeight: isCurrent ? 600 : 400,
                              }}
                              onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.background = '#f8fafc'; }}
                              onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.background = 'transparent'; }}
                            >
                              <span style={{
                                display: 'inline-block',
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: settings?.status_colors?.[st] || '#6b7280',
                                flexShrink: 0,
                              }} />
                              <span>{st}</span>
                              {isCurrent && <span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '0.7rem' }}>current</span>}
                            </button>
                          );
                        })}
                      </PopoverContent>
                    </Popover>
                  </TableCell>
                  {/* Progress Status pill — only rendered when actively "in flight".
                      Colors match the Dashboard operational tiles so the eye learns
                      the mapping (blue = Imaging, amber = WIP, red = Awaiting Parts).
                      "Not Started" is intentionally omitted to reduce visual noise. */}
                  <TableCell>
                    {(() => {
                      const ps = job.progress_status;
                      if (!ps || ps === 'Not Started') return <span style={{ color: '#cbd5e1', fontSize: '0.75rem' }}>—</span>;
                      const styles = {
                        'Imaging':        { bg: '#e0f2fe', fg: '#075985', icon: '💾' },
                        'WIP':            { bg: '#fef3c7', fg: '#92400e', icon: '🛠' },
                        'Awaiting Parts': { bg: '#fee2e2', fg: '#991b1b', icon: '📦' },
                      }[ps] || { bg: '#f1f5f9', fg: '#334155', icon: '' };
                      return (
                        <span
                          data-testid={`job-progress-${job.job_number}`}
                          style={{
                            display: 'inline-block',
                            padding: '0.25rem 0.6rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            backgroundColor: styles.bg,
                            color: styles.fg,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {styles.icon} {ps}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const invoiceSentAt = emailStatusByJob?.[job.id]?.invoice?.sent_at || null;
                      const info = computeDaysOwedInfo(job, invoiceSentAt);
                      if (!info.show) {
                        return <span style={{ color: '#94a3b8', fontSize: '0.8rem' }} data-testid={`days-owed-empty-${job.job_number}`}>—</span>;
                      }
                      const dayLabel = info.days === 0 ? 'Today' : (info.days === 1 ? '1 day' : `${info.days} days`);
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'nowrap' }}>
                          <span
                            data-testid={`days-owed-${job.job_number}`}
                            title={`Balance $${Number(info.balance).toFixed(2)} owing • ${dayLabel} since ${job.date_finished ? 'finished' : (job.date_out ? 'date out' : 'date in')}${info.neverInvoiced ? ' • invoice not yet emailed' : ''}`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '0.2rem 0.55rem',
                              borderRadius: '9999px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              backgroundColor: info.bg,
                              color: info.fg,
                              border: `1px solid ${info.border}`,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {info.band === 'critical' ? '🔴' : (info.band === 'warning' ? '🟡' : '⚪')} {dayLabel}
                            {info.neverInvoiced && (
                              <span
                                data-testid={`never-invoiced-${job.job_number}`}
                                title="No invoice email has been sent for this job yet"
                                style={{ marginLeft: '4px', fontSize: '0.7rem', color: '#b45309' }}
                              >⚠ Not invoiced</span>
                            )}
                          </span>
                          {job.mobile && settings?.twilio_enabled && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openPaymentReminderSms(job)}
                              data-testid={`payment-reminder-sms-${job.job_number}`}
                              title="Send payment reminder SMS"
                              style={{ padding: '0 6px', height: '24px', fontSize: '0.85rem' }}
                            >📱</Button>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell>{formatDate(job.date_in)}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleCloseJob(job)}
                      data-testid={`close-job-${job.job_number}`}
                    >
                      Close
                    </Button>
                  </TableCell>
                  <TableCell>
                    <div className="action-buttons" style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      {/* View button removed Feb 2026 — clicking the Job # in
                          the first column already navigates to the same
                          detail page, so this button was redundant. */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => printSticker(job)}
                        data-testid={`print-sticker-${job.job_number}`}
                        title="Print Sticker"
                        style={{ width: '32px', height: '32px', padding: 0, color: '#475569' }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                          <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82Z"/>
                          <line x1="7" y1="7" x2="7.01" y2="7"/>
                        </svg>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => printFullForm(job)}
                        data-testid={`print-form-${job.job_number}`}
                        title="Print Full Form"
                        style={{ width: '32px', height: '32px', padding: 0, color: '#475569' }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                          <polyline points="6 9 6 2 18 2 18 9"/>
                          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                          <rect x="6" y="14" width="12" height="8"/>
                        </svg>
                      </Button>
                      {/* Feb 2026 — Contacted pill collapsed to a phone icon
                          only. Same behaviour: green fill when ticked
                          (with tooltip audit trail), grey outline when
                          not. Auto-resets whenever the job's status
                          changes (see update_job in the backend). */}
                      <Button
                        type="button"
                        size="sm"
                        variant={job.contacted ? 'default' : 'outline'}
                        onClick={(e) => { e.stopPropagation(); handleToggleContacted(job.id); }}
                        data-testid={`contacted-toggle-${job.job_number}`}
                        title={job.contacted
                          ? `Contacted${job.contacted_at ? ` on ${new Date(job.contacted_at).toLocaleString('en-AU')}` : ''}${job.contacted_by ? ` by ${job.contacted_by}` : ''}${job.contacted_at_status ? ` — status was '${job.contacted_at_status}'` : ''}. Click to un-tick.`
                          : 'Mark as contacted (auto-clears on status change).'}
                        style={{
                          width: '32px',
                          height: '32px',
                          padding: 0,
                          backgroundColor: job.contacted ? '#16a34a' : 'white',
                          borderColor:     job.contacted ? '#16a34a' : '#cbd5e1',
                          color:           job.contacted ? 'white'    : '#475569',
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                        </svg>
                      </Button>
                      {/* Feb 2026 — coloured icons showing the latest Brevo
                          status for quote / invoice / collection_notice
                          emails sent from this job. Hidden entirely when
                          no tracked email has been sent yet. */}
                      <EmailStatusIcons
                        summary={emailStatusByJob[job.id]}
                        jobId={job.id}
                        apiBase={API}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {visibleJobs.length === 0 && (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280', fontSize: '0.9rem' }} data-testid="chip-empty-state">
              No jobs match this filter. <button
                type="button"
                onClick={() => setPaymentFlagFilter('all')}
                data-testid="chip-empty-clear"
                style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}
              >
                Clear chip
              </button>
            </div>
          )}
        </div>
      )}

      {/* Set Reminder Modal */}
      <Dialog open={showReminderModal} onOpenChange={setShowReminderModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Set New Reminder</DialogTitle>
            <DialogDescription>
              Create a general reminder for tasks not related to a specific job.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
            <div className="form-field">
              <Label htmlFor="reminder-title">Task Name *</Label>
              <Input
                id="reminder-title"
                placeholder="e.g., Call supplier about delivery"
                value={newReminder.title}
                onChange={(e) => setNewReminder({ ...newReminder, title: e.target.value })}
                data-testid="reminder-title-input"
              />
            </div>
            
            <div className="form-field">
              <Label htmlFor="reminder-description">Description (optional)</Label>
              <Textarea
                id="reminder-description"
                placeholder="Additional details..."
                value={newReminder.description}
                onChange={(e) => setNewReminder({ ...newReminder, description: e.target.value })}
                rows={2}
                data-testid="reminder-description-input"
              />
            </div>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <div className="form-field" style={{ flex: 1 }}>
                <Label htmlFor="reminder-date">Due Date *</Label>
                <Input
                  id="reminder-date"
                  type="date"
                  value={newReminder.due_date}
                  onChange={(e) => setNewReminder({ ...newReminder, due_date: e.target.value })}
                  data-testid="reminder-date-input"
                />
              </div>
              
              <div className="form-field" style={{ flex: 1 }}>
                <Label htmlFor="reminder-time">Due Time (optional)</Label>
                <Input
                  id="reminder-time"
                  type="time"
                  value={newReminder.due_time}
                  onChange={(e) => setNewReminder({ ...newReminder, due_time: e.target.value })}
                  data-testid="reminder-time-input"
                />
              </div>
            </div>
            
            <div className="form-field">
              <Label htmlFor="reminder-priority">Priority</Label>
              <Select
                value={newReminder.priority}
                onValueChange={(value) => setNewReminder({ ...newReminder, priority: value })}
              >
                <SelectTrigger id="reminder-priority" data-testid="reminder-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter style={{ marginTop: '1.5rem' }}>
            <Button variant="outline" onClick={() => setShowReminderModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateReminder}
              data-testid="create-reminder-btn"
              style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}
            >
              Create Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revenue Jobs Drill-Down Modal */}
      <Dialog open={revenueJobsModal.open} onOpenChange={(open) => setRevenueJobsModal({ ...revenueJobsModal, open })}>
        <DialogContent style={{ maxWidth: '900px', maxHeight: '85vh', overflow: 'auto' }}>
          <DialogHeader>
            <DialogTitle>{revenueJobsModal.title}</DialogTitle>
          </DialogHeader>
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '1.5rem', flexWrap: 'wrap', padding: '0.75rem 1rem', backgroundColor: '#f8fafc', borderRadius: '0.5rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Total Revenue (inc GST)</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#16a34a' }}>
                ${revenueJobsModal.total_revenue.toLocaleString('en-AU', { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Jobs</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#2563eb' }}>
                {revenueJobsModal.total_jobs}
              </div>
            </div>
          </div>
          {revenueJobsLoading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading...</div>
          ) : revenueJobsModal.jobs.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
              No completed jobs in this period.
            </div>
          ) : (
            <div style={{ overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem', fontWeight: '600' }}>Job #</th>
                    <th style={{ padding: '0.5rem', fontWeight: '600' }}>Customer</th>
                    <th style={{ padding: '0.5rem', fontWeight: '600' }}>Type</th>
                    <th style={{ padding: '0.5rem', fontWeight: '600' }}>Paid on</th>
                    <th style={{ padding: '0.5rem', fontWeight: '600', textAlign: 'right' }}>Total (inc GST)</th>
                    <th style={{ padding: '0.5rem', fontWeight: '600', textAlign: 'right', color: '#94a3b8' }}>Subtotal (ex GST)</th>
                    <th style={{ padding: '0.5rem', fontWeight: '600' }}>Payment</th>
                    <th style={{ padding: '0.5rem', fontWeight: '600' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {revenueJobsModal.jobs.map((j) => (
                    <tr key={j.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.5rem', fontWeight: '600' }}>{j.job_number}</td>
                      <td style={{ padding: '0.5rem' }}>{j.customer_name}</td>
                      <td style={{ padding: '0.5rem' }}>{j.job_type}</td>
                      <td style={{ padding: '0.5rem', color: '#6b7280' }}>
                        {j.revenue_date ? new Date(j.revenue_date).toLocaleDateString('en-AU') : (j.date_finished ? new Date(j.date_finished).toLocaleDateString('en-AU') : '-')}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600' }}>
                        ${parseFloat(j.revenue_amount || j.total_amount || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', color: '#94a3b8' }}>
                        ${parseFloat(j.subtotal || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        <span style={{
                          padding: '0.15rem 0.5rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          backgroundColor: j.payment_status === 'Paid' ? '#dcfce7' : j.payment_status === 'Partial' ? '#fef3c7' : '#fee2e2',
                          color: j.payment_status === 'Paid' ? '#166534' : j.payment_status === 'Partial' ? '#92400e' : '#991b1b',
                        }}>
                          {j.payment_status || 'Unpaid'}
                        </span>
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setRevenueJobsModal({ ...revenueJobsModal, open: false });
                            navigate(`/job/${j.id}`);
                          }}
                        >
                          Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter style={{ marginTop: '1rem' }}>
            <Button variant="outline" onClick={() => setRevenueJobsModal({ ...revenueJobsModal, open: false })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Jobs Due Soon — full-list dialog */}
      <Dialog open={dueBucketDialog.open} onOpenChange={(open) => setDueBucketDialog({ ...dueBucketDialog, open })}>
        <DialogContent style={{ maxWidth: '720px', maxHeight: '85vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle>
              <span style={{ marginRight: '0.5rem' }}>{dueBucketDialog.icon}</span>
              {dueBucketDialog.title} ({dueBucketDialog.jobs.length} {dueBucketDialog.jobs.length === 1 ? 'job' : 'jobs'})
            </DialogTitle>
            <DialogDescription>Click any job to open its detail page.</DialogDescription>
          </DialogHeader>
          {dueBucketDialog.jobs.length === 0 ? (
            <p style={{ color: '#64748b', textAlign: 'center', padding: '1.5rem' }}>No jobs in this bucket.</p>
          ) : (
            <div style={{ maxHeight: '60vh', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Due Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dueBucketDialog.jobs.map(job => (
                    <TableRow
                      key={job.id}
                      onClick={() => { setDueBucketDialog({ ...dueBucketDialog, open: false }); navigate(`/job/${job.id}`); }}
                      style={{ cursor: 'pointer' }}
                      data-testid={`due-dialog-job-${job.job_number}`}
                    >
                      <TableCell style={{ fontWeight: 600, color: '#2563eb' }}>#{job.job_number}</TableCell>
                      <TableCell>{job.customer_name}</TableCell>
                      <TableCell>{job.status}</TableCell>
                      <TableCell>{formatDate(job.date_due)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter style={{ marginTop: '1rem' }}>
            <Button variant="outline" onClick={() => setDueBucketDialog({ ...dueBucketDialog, open: false })} data-testid="due-dialog-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — Payment Reminder SMS modal — invoked from the 📱 button
          on each Days Owed cell. Pre-fills the "Payment Reminder" template
          (customer name, job number, balance) but is fully editable before
          send. Reuses POST /api/sms/send. */}
      <Dialog open={smsModalOpen} onOpenChange={setSmsModalOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Send Payment Reminder to {smsJob?.customer_name}</DialogTitle>
            <DialogDescription>
              Balance owing: <strong>${parseFloat(smsJob?.balance_owing ?? ((smsJob?.total_amount || 0) - (smsJob?.amount_paid || 0))).toFixed(2)}</strong>
              {smsJob?.job_number ? ` • Job #${smsJob.job_number}` : ''}
            </DialogDescription>
          </DialogHeader>

          <div style={{ display: 'grid', gap: '1rem', padding: '0.5rem 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#f9fafb', padding: '0.6rem 0.75rem', borderRadius: '6px', fontSize: '0.85rem' }}>
              <span style={{ color: '#6b7280' }}>To</span>
              <span style={{ fontWeight: 500 }}>{smsJob?.mobile || '—'}</span>
            </div>

            {smsTemplates.length > 0 && (
              <div>
                <Label>Quick Templates</Label>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
                  {smsTemplates.map((template, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      onClick={() => applySmsTemplate(template)}
                      data-testid={`sms-template-${idx}`}
                    >
                      {template.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="payment-reminder-sms-message">Message</Label>
              <textarea
                id="payment-reminder-sms-message"
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                placeholder="Type your message…"
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  fontFamily: 'inherit',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                }}
                data-testid="payment-reminder-sms-input"
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {smsMessage.length}/160 characters{smsMessage.length > 160 ? ` (${Math.ceil(smsMessage.length / 160)} SMS)` : ''}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSmsModalOpen(false)}>Cancel</Button>
            <Button onClick={sendPaymentReminderSms} disabled={sendingSms || !smsMessage.trim()} data-testid="payment-reminder-send-btn">
              {sendingSms ? 'Sending…' : 'Send SMS'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Feb 2026 — SMS Inbox thread modal. Shows the full in/out SMS
          conversation for a job + inline reply box. */}
      <Dialog open={smsThreadOpen} onOpenChange={setSmsThreadOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              💬 SMS Conversation — #{smsThreadData?.job_number} {smsThreadData?.customer_name}
            </DialogTitle>
            <DialogDescription>
              {smsThreadData?.mobile || '—'} · {smsThreadData?.status || ''}
            </DialogDescription>
          </DialogHeader>

          <div
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
              padding: '0.5rem',
              background: '#f8fafc',
              borderRadius: '6px',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
            }}
            data-testid="sms-thread-message-list"
          >
            {(smsThreadData?.messages || []).length === 0 ? (
              <div style={{ color: '#9ca3af', textAlign: 'center', padding: '1.5rem', fontSize: '0.85rem' }}>
                No messages yet
              </div>
            ) : (
              (smsThreadData?.messages || []).map((m, idx) => {
                const isInbound = m.type === 'sms_received';
                const ts = m.timestamp ? new Date(m.timestamp).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', dateStyle: 'short', timeStyle: 'short' }) : '';
                return (
                  <div
                    key={idx}
                    style={{
                      alignSelf: isInbound ? 'flex-start' : 'flex-end',
                      maxWidth: '78%',
                      background: isInbound ? '#ffffff' : '#dbeafe',
                      border: `1px solid ${isInbound ? '#e2e8f0' : '#93c5fd'}`,
                      borderRadius: '10px',
                      padding: '0.5rem 0.7rem',
                      fontSize: '0.85rem',
                    }}
                  >
                    <div style={{ color: '#334155', whiteSpace: 'pre-wrap' }}>{m.message || ''}</div>
                    <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '0.2rem', textAlign: isInbound ? 'left' : 'right' }}>
                      {isInbound ? '← incoming' : 'you →'} · {ts}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div style={{ marginTop: '0.75rem' }}>
            <Label htmlFor="sms-inbox-reply">Your reply</Label>
            <textarea
              id="sms-inbox-reply"
              value={smsReplyText}
              onChange={(e) => setSmsReplyText(e.target.value)}
              placeholder={`Reply to ${smsThreadData?.customer_name || 'customer'}…`}
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '0.6rem',
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                fontFamily: 'inherit',
                fontSize: '0.875rem',
                resize: 'vertical',
              }}
              data-testid="sms-thread-reply-input"
            />
            <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.25rem' }}>
              {smsReplyText.length}/160 characters{smsReplyText.length > 160 ? ` (${Math.ceil(smsReplyText.length / 160)} SMS)` : ''}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSmsThreadOpen(false)}>Close</Button>
            <Button
              onClick={sendSmsReplyFromInbox}
              disabled={smsReplySending || !smsReplyText.trim()}
              data-testid="sms-thread-send-btn"
              style={{ backgroundColor: '#6366f1', color: 'white' }}
            >
              {smsReplySending ? 'Sending…' : 'Send Reply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default Dashboard;
